import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Grid, Alert, LinearProgress,
  Divider, IconButton, Tooltip, Chip, Card, CardContent, Pagination, Slider,
  ToggleButton, ToggleButtonGroup, Tabs, Tab, Dialog, DialogTitle,
  DialogContent, DialogActions, Menu, MenuItem, ListItemIcon, ListItemText,
  Checkbox, Switch, CircularProgress
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import {
  FaVideo, FaDownload, FaTimes, FaHistory, FaPlay, FaImages,
  FaBookmark, FaRegBookmark, FaFolder, FaFolderOpen, FaPlus, FaEdit, FaTrash, FaGlobe, FaLock, FaChair, FaBorderAll,
  FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import { CloudUpload, CheckCircle, Clear, Videocam, Fullscreen, ContentCopy, ExpandMore, ExpandLess, Check, AutoAwesome, AutoFixHigh, Edit as EditIcon, CallMerge, ContentCut, FactCheck } from '@mui/icons-material';
import { MdFolderSpecial, MdCompare, MdAddCircleOutline } from 'react-icons/md';
import { useSnackbar } from 'notistack';
import API_BASE_URL, { apiCall, API_ENDPOINTS } from '../config/api';
import { TOOL_COLORS } from '../constants/colors';
import { formatDateTime } from '../utils/dateUtils';
import InterfaceHeader from '../components/InterfaceHeader';

const AI_COLOR = TOOL_COLORS.AI.primary;

// Google API cost per second of generated video (USD). A visible estimate beats the
// invisible-spend trap documented in AI_PHOTO_PIPELINE_COSTS.md.
//  - Veo 3.1 runs on Google Cloud (Vertex AI) since 2026-10-31 - list prices from the Agent
//    Platform pricing page, read 2026-10-31. Sound on / off (silent costs less):
//      Veo 3.1 (veo-3.1-generate-001):           720p & 1080p $0.40 / $0.20 · 4K $0.60 / $0.40
//      Veo 3.1 Fast (veo-3.1-fast-generate-001): 720p $0.10 / $0.08 · 1080p $0.12 / $0.10 (no 4K)
//  - Gemini Omni 1.1 Flash: $17.50 per 1M video-output tokens. Token use per second was
//    MEASURED from the API's own usage report on 2026-09-02 (a linear pixel model was 41%
//    wrong at 1080p, so each tier is measured rather than extrapolated):
//      360p 1,931 tok/s · 720p 5,792 (Google's documented figure) · 1080p 8,688 · 4K 17,376
//    Omni always has sound. Omni 4K ($0.30/s) is about half the price of Veo 3.1 4K ($0.60/s).
const VEO_RATES_PER_SECOND = {
  veo: {
    sound: { '720p': 0.40, '1080p': 0.40, '4k': 0.60 },
    silent: { '720p': 0.20, '1080p': 0.20, '4k': 0.40 },
  },
  'veo-fast': {
    sound: { '720p': 0.10, '1080p': 0.12 },
    silent: { '720p': 0.08, '1080p': 0.10 },
  },
};
const OMNI_RATES_PER_SECOND = { '360p': 0.034, '720p': 0.101, '1080p': 0.152, '4k': 0.304 };
const isVeoEngine = (e) => e === 'veo' || e === 'veo-fast';
// Labels also cover engines of older History rows (Lite, Seedance - no longer offered).
const engineLabel = (e) =>
  e === 'omni' ? 'Omni 1.1 Flash'
  : e === 'seedance' ? 'Seedance 2.0'
  : e === 'veo-fast' ? 'Veo 3.1 Fast'
  : e === 'veo-lite' ? 'Veo 3.1 Lite'
  : 'Veo 3.1';
const estimateVideoCost = (engine, resolution, duration, sound = true) => {
  const res = String(resolution).toLowerCase();
  if (engine === 'omni') return (OMNI_RATES_PER_SECOND[res] ?? OMNI_RATES_PER_SECOND['720p']) * duration;
  const veo = VEO_RATES_PER_SECOND[engine] ?? VEO_RATES_PER_SECOND.veo;
  const table = sound ? veo.sound : veo.silent;
  return (table[res] ?? table['720p']) * duration;
};

// Product-fidelity QC badge color: green = faithful, amber = check it, red = drifted.
const fidelityColor = (score) => (score >= 8 ? '#4caf50' : score >= 5 ? '#ff9800' : '#f44336');
const parseFidelity = (item) => {
  try { return item?.fidelityJson ? JSON.parse(item.fidelityJson) : null; } catch { return null; }
};

const DEFAULT_PROMPTS_FURNITURE = [
  { name: 'Living Room Pan', text: 'Cinematic slow camera pan showcasing the Safavieh furniture piece in a beautifully styled modern living room. The product from the reference image must remain EXACTLY as shown - do not alter its design, color, texture, or proportions in any way. Product dimensions: [INSERT DIMENSIONS]. Soft warm natural lighting highlights the craftsmanship and material quality. Smooth dolly movement past coordinating decor. Professional furniture commercial style. 4K quality.' },
  { name: 'Styled Room Reveal', text: 'A gentle overhead camera slowly descends to reveal the Safavieh furniture piece perfectly placed in an elegantly designed room setting. The product must look IDENTICAL to the reference image - preserve every detail of its design, finish, and color. Show the piece alongside complementary furniture and decor to demonstrate scale and style. Natural sunlight streams through windows creating warm highlights. Cinematic home furnishing showcase quality.' },
  { name: '360 Product Orbit', text: 'Slow cinematic orbit shot around the Safavieh furniture piece placed in a beautifully staged contemporary interior. The product must be EXACTLY as shown in the reference image - same design, same finish, same colors, same proportions. Camera smoothly circles to reveal the piece from multiple angles showing construction details, material textures, and design features. Warm ambient lighting with soft depth of field. Premium furniture commercial style.' },
  { name: 'Detail Close-Up', text: 'The camera starts on an extreme close-up of the Safavieh furniture piece showing material texture, finish quality, and craftsmanship details, then slowly pulls back to reveal the full piece in a professionally styled room. The product must remain UNCHANGED from the reference image. Show wood grain, fabric texture, or metalwork details up close. Cinematic lighting with warm tones. Smooth crane movement. Professional furniture showcase quality.' },
  { name: 'Lifestyle Scene', text: 'Create a warm lifestyle video of the Safavieh furniture piece in a welcoming home environment. The product from the reference image must remain EXACTLY as shown - do not modify its appearance in any way. Show the piece being naturally used in context - a family gathering around a dining table, someone relaxing on a sofa, or a beautifully set entryway. Soft golden hour lighting. Slow motion details. Aspirational home lifestyle commercial.' },
];

const DEFAULT_PROMPTS_RUGS = [
  { name: 'Rug Room Showcase', text: 'Cinematic slow camera pan across a beautifully furnished living room with the Safavieh area rug as the centerpiece. The rug from the reference image must remain EXACTLY as shown - do not alter its pattern, colors, or texture in any way. Rug dimensions: [INSERT DIMENSIONS]. Camera glides low showing the rug texture and how it anchors the seating arrangement. Warm natural lighting. Professional home decor commercial style. 4K quality.' },
  { name: 'Overhead Rug Reveal', text: 'A gentle overhead camera slowly descends to reveal the Safavieh area rug perfectly placed in a styled room. The rug must look IDENTICAL to the reference image - preserve every detail of the pattern, weave, and color palette. Show the rug with coordinating furniture to demonstrate scale. Natural sunlight creates warm highlights across the rug surface showing texture depth. Cinematic home staging showcase quality.' },
  { name: 'Texture Detail Pan', text: 'The camera starts with an extreme close-up of the Safavieh rug showing weave texture, pile depth, and color detail, then slowly pulls back to reveal the full rug in a professionally styled room. The rug must remain UNCHANGED from the reference image. Show the intricate pattern details, fiber quality, and craftsmanship up close. Cinematic lighting with warm tones. Smooth crane movement. Professional rug showcase quality.' },
  { name: 'Lifestyle Rug Scene', text: 'Create a warm lifestyle video of the Safavieh area rug in a welcoming home setting. The rug from the reference image must remain EXACTLY as shown - do not modify its appearance. Show the rug naturally in context - children playing, a cozy reading nook, or a stylish dining area. Bare feet on the rug to show softness. Soft golden hour lighting. Slow motion lifestyle details. Aspirational home decor commercial.' },
  { name: 'Walk-Through Reveal', text: 'Cinematic walk-through shot entering a beautifully decorated room where the Safavieh area rug ties the space together. The rug must be EXACTLY as shown in the reference image - same pattern, same colors, same proportions. Camera moves through the doorway and slowly reveals the room with the rug as the focal point. Warm ambient lighting with soft depth of field. Professional interior design showcase.' },
];

const AIVideoGenerator = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { enqueueSnackbar } = useSnackbar();
  // Brand merlot everywhere, per Klevi — keep the AI tool color identity intact
  // in both modes. (Single knob if dark-mode contrast ever needs revisiting.)
  const AI_TEXT = AI_COLOR;
  const logEndRef = useRef(null);
  const intervalRef = useRef(null);

  // Main tab state
  const [mainTab, setMainTab] = useState('generator');
  const [videoMode, setVideoMode] = useState('Furniture');
  const DEFAULT_PROMPTS = videoMode === 'Rugs' ? DEFAULT_PROMPTS_RUGS : DEFAULT_PROMPTS_FURNITURE;

  // Form state
  const [images, setImages] = useState([]);
  const [prompt, setPrompt] = useState(DEFAULT_PROMPTS_FURNITURE[0].text);
  const [activePromptName, setActivePromptName] = useState('Living Room Pan');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState('4k');
  const [duration, setDuration] = useState(8);
  const [engine, setEngine] = useState('veo'); // 'veo' = Veo 3.1 | 'veo-fast' = Veo 3.1 Fast | 'omni' = Gemini Omni Flash
  const [firstFrame, setFirstFrame] = useState(false);       // Veo: 1 photo becomes the literal first frame
  const [interpolate, setInterpolate] = useState(false); // Veo: image1 = first frame, image2 = last frame
  const [veoSound, setVeoSound] = useState(true);        // Veo: generate sound (silent costs less)
  const [extendOpen, setExtendOpen] = useState(false);   // "Extend +7s" dialog in the player
  const [extendPrompt, setExtendPrompt] = useState('');
  const [extendLoading, setExtendLoading] = useState(false);
  const [fixItEditInPlace, setFixItEditInPlace] = useState(true); // Omni conversational edit toggle

  // Process state
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processId, setProcessId] = useState(null);

  // Result state
  const [results, setResults] = useState(null);
  // The reference images the CURRENT result was generated from (own blob URLs, so removing or
  // replacing uploads afterwards never changes what the compare view shows for that video).
  const [resultInputs, setResultInputs] = useState([]);
  const [compareMode, setCompareMode] = useState(true); // show generated video side-by-side with reference input
  const [compareDialogItem, setCompareDialogItem] = useState(null); // { s3Url, inputImages:[] } shown in the fullscreen compare modal
  const [compareSplit, setCompareSplit] = useState('50'); // '50' = half/half, '75' = video 75% / image 25%
  const [zoomImageUrl, setZoomImageUrl] = useState(null);  // reference image opened in the click-to-zoom lightbox
  const [zoomImageList, setZoomImageList] = useState([]);  // sibling images for prev/next navigation in the lightbox
  const [zoomActive, setZoomActive] = useState(false);

  // Open the full-size viewer on list[idx] ({src,label} entries), remembering siblings for navigation
  const openImagePreview = (list, idx) => {
    const items = (Array.isArray(list) ? list : []).map(it => (typeof it === 'string' ? { src: it, label: '' } : it));
    setZoomImageList(items);
    setZoomImageUrl(items[idx]?.src || null);
    setZoomActive(false);
  };
  const stepImagePreview = (dir) => {
    if (zoomImageList.length < 2) return;
    const cur = zoomImageList.findIndex(i => i.src === zoomImageUrl);
    const next = ((cur < 0 ? 0 : cur) + dir + zoomImageList.length) % zoomImageList.length;
    setZoomImageUrl(zoomImageList[next].src);
    setZoomActive(false);
  };
  // Human-friendly filename from an S3/object URL
  const fileNameFromUrl = (url) => {
    try { return decodeURIComponent((url || '').split('?')[0].split('/').pop() || ''); } catch { return ''; }
  };

  // Left/Right arrow keys navigate the lightbox while it is open
  useEffect(() => {
    if (!zoomImageUrl || zoomImageList.length < 2) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') stepImagePreview(-1);
      else if (e.key === 'ArrowRight') stepImagePreview(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomImageUrl, zoomImageList]);
  const [inplaceZoomIdx, setInplaceZoomIdx] = useState(null); // reference image zoomed in place within the compare view

  // History state
  const [historyItems, setHistoryItems] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const historyPageSize = 12;
  const [playingHistoryIndex, setPlayingHistoryIndex] = useState(-1); // fullscreen player w/ prev-next scrolling
  const [playerPromptExpanded, setPlayerPromptExpanded] = useState(false); // full prompt in the history player footer
  const [playerPromptCopied, setPlayerPromptCopied] = useState(false);     // copy button briefly shows a check
  const playerPromptCopiedTimer = useRef(null);
  const playerVideoRef = useRef(null); // pause the player when opening the reference-compare overlay
  const [fidelityDetail, setFidelityDetail] = useState(null); // parsed QC rubric shown in the detail dialog
  const [fidelityChecking, setFidelityChecking] = useState(false);

  // AI Director (Gemini writes 3 prompt options from the product images) + Fix It
  // (guided re-prompt from a bad video in the history player)
  const [directorLoading, setDirectorLoading] = useState(false);
  const [directorUseIdea, setDirectorUseIdea] = useState(false);   // toggle: build on the current prompt vs images-only
  const [directorPrompts, setDirectorPrompts] = useState([]);      // the 3 returned {name, text} options
  const [directorDialogOpen, setDirectorDialogOpen] = useState(false);
  const [directorSavedIdx, setDirectorSavedIdx] = useState([]);    // indexes already saved to the prompt library
  const [directorSavingIdx, setDirectorSavingIdx] = useState(-1);  // index currently saving
  const [directorCopiedIdx, setDirectorCopiedIdx] = useState(-1);  // copy button briefly shows a check
  const directorCopiedTimer = useRef(null);
  const [mainPromptCopied, setMainPromptCopied] = useState(false); // copy button on the main prompt textbox
  const mainPromptCopiedTimer = useRef(null);
  const [fixItOpen, setFixItOpen] = useState(false);
  const [fixItIssues, setFixItIssues] = useState([]);
  const [fixItNotes, setFixItNotes] = useState('');
  const [fixItLoading, setFixItLoading] = useState(false);
  const [historyEngineFilter, setHistoryEngineFilter] = useState(''); // '' = all, 'veo', 'omni'
  const [historyCompareMode, setHistoryCompareMode] = useState(false); // pick 2 videos to watch side by side
  const [historyCompareSel, setHistoryCompareSel] = useState([]);      // the selected history items (max 2)
  const [historyCompareOpen, setHistoryCompareOpen] = useState(false);

  // Video editor (trim) + merge
  const [editorItem, setEditorItem] = useState(null);   // the history item being edited
  const [editorVideoDur, setEditorVideoDur] = useState(8);
  const [editTrim, setEditTrim] = useState([0, 8]);     // [start, end] seconds
  const [editApplying, setEditApplying] = useState(false);
  const editorVideoRef = useRef(null);                  // scrub the preview while dragging trim handles
  const editorPreviewStopAt = useRef(null);             // pause point for "preview trim" playback
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSel, setMergeSel] = useState([]);         // items in the order they were picked
  const [mergeApplying, setMergeApplying] = useState(false);

  // Collections state
  const [collections, setCollections] = useState(() => JSON.parse(localStorage.getItem('ai_video_collections') || '[]'));
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [showCreateCollection, setShowCreateCollection] = useState(false);
  const [editingCollectionId, setEditingCollectionId] = useState(null);
  const [editingCollectionName, setEditingCollectionName] = useState('');
  const [collectionCompareMode, setCollectionCompareMode] = useState(false);
  const [collectionCompareSelected, setCollectionCompareSelected] = useState(new Set());
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Add videos to collection picker
  const [addVideosDialogOpen, setAddVideosDialogOpen] = useState(false);
  const [addVideosItems, setAddVideosItems] = useState([]);
  const [addVideosPreviewId, setAddVideosPreviewId] = useState(null); // video playing inline in the add-videos dialog
  const [addVideosLoading, setAddVideosLoading] = useState(false);
  const [addVideosPage, setAddVideosPage] = useState(1);
  const [addVideosTotalPages, setAddVideosTotalPages] = useState(1);

  // Save to collection menu
  const [saveMenuAnchor, setSaveMenuAnchor] = useState(null);
  const [saveMenuVideoData, setSaveMenuVideoData] = useState(null);
  const [showCreateInMenu, setShowCreateInMenu] = useState(false); // inline "new collection" field in the save menu
  const [newColNameInMenu, setNewColNameInMenu] = useState('');

  // Prompt Library state (server-backed)
  const [prompts, setPrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editPromptName, setEditPromptName] = useState('');
  const [editPromptText, setEditPromptText] = useState('');
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);
  const [editPromptIsPublic, setEditPromptIsPublic] = useState(false);
  const [promptSaveError, setPromptSaveError] = useState('');

  // Sync collections to localStorage AND backend whenever they change
  useEffect(() => {
    localStorage.setItem('ai_video_collections', JSON.stringify(collections));
    // Save to backend (debounced via the effect)
    apiCall(`${API_BASE_URL}/api/AIVideoGenerator/collections`, {
      method: 'POST',
      body: JSON.stringify({ collections }),
    }).catch(() => {}); // silently fail
  }, [collections]);

  // Load collections from backend on mount (overrides localStorage if server has data)
  useEffect(() => {
    const loadServerCollections = async () => {
      try {
        const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/collections`);
        if (data.success && data.collections && data.collections.length > 0) {
          setCollections(data.collections);
        } else {
          // One-time migration: push localStorage to server if server is empty
          const local = JSON.parse(localStorage.getItem('ai_video_collections') || '[]');
          if (local.length > 0) {
            await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/collections`, {
              method: 'POST',
              body: JSON.stringify({ collections: local }),
            }).catch(() => {});
          }
        }
      } catch { /* use localStorage fallback */ }
    };
    loadServerCollections();
  }, []); // eslint-disable-line

  // Engine limits: Veo accepts at most 3 asset reference images;
  // Omni Flash allows up to 10 (bounded by its ~14MB inline size budget).
  // Selection itself is never blocked — going over a limit shows a warning
  // and disables Generate, so the user decides what to remove.
  const maxImages = engine === 'omni' ? 10 : 3;
  const refTotalMB = images.reduce((sum, f) => sum + (f.size || 0), 0) / 1048576;
  // The upload endpoint caps the whole request at 100MB.
  const overImageLimit = images.length > maxImages || (engine === 'omni' && refTotalMB > 14) || refTotalMB > 95;

  // Dropzone
  const onDrop = useCallback((acceptedFiles) => {
    const newImages = acceptedFiles.map(f =>
      Object.assign(f, { preview: URL.createObjectURL(f) })
    );
    setImages(prev => [...prev, ...newImages]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.tif', '.tiff'] },
    disabled: loading,
  });

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      images.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
      resultInputs.forEach(i => { if (i.preview) URL.revokeObjectURL(i.preview); });
    };
  }, []); // eslint-disable-line

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Auto-scroll logs
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [logs]);

  // Fetch history
  const fetchHistory = useCallback(async (page = 1) => {
    try {
      const engineParam = historyEngineFilter ? `&engine=${historyEngineFilter}` : '';
      const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/history?page=${page}&pageSize=${historyPageSize}${engineParam}`);
      setHistoryItems(data.generations || data.items || []);
      setHistoryTotal(data.totalItems || data.totalCount || 0);
      setHistoryPage(data.page || page);
    } catch (err) {
      console.error('Failed to load video history:', err);
    }
  }, [historyEngineFilter]);

  // Load history on mount
  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  // Load history when switching to history tab
  useEffect(() => {
    if (mainTab === 'history') {
      fetchHistory(1);
    }
  }, [mainTab, fetchHistory]);

  // Remove image
  const removeImage = useCallback((idx) => {
    setImages(prev => {
      const removed = prev[idx];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  // Poll status
  const pollStatus = useCallback(async (pid) => {
    try {
      const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/status/${pid}`);

      if (data.progressPercentage !== undefined) {
        setProgress(data.progressPercentage);
      }

      if (data.logs && data.logs.length > 0) {
        setLogs(data.logs);
      }

      if (data.status === 'completed' || data.status === 'Completed') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setResults({
          s3Url: data.s3Url,
          videoFileName: data.videoFileName,
          generationId: data.generationId,
        });
        setSuccess('Video generated successfully!');
        setLoading(false);
        setProgress(100);
        fetchHistory(1);
      } else if (data.status === 'error' || data.status === 'Error' || data.status === 'failed' || data.status === 'Failed') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setError(data.errorMessage || 'Video generation failed.');
        setLoading(false);
        setProgress(0);
      }
    } catch (err) {
      console.error('Error polling status:', err);
    }
  }, [fetchHistory]);

  // Attach the progress UI to a server-side job (generate / extend / omni-edit
  // all share this). The render itself always finishes server-side regardless
  // of the browser — this only drives the progress view.
  const startPollingJob = useCallback((pid, startedAt = Date.now(), kickLog = null, inputs = null) => {
    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setResultInputs(prev => {
      prev.forEach(i => { if (i.preview) URL.revokeObjectURL(i.preview); });
      return inputs || [];
    });
    setProcessId(pid);
    setProgress(0);
    if (kickLog) setLogs([`[${new Date().toLocaleTimeString()}] ${kickLog}`]);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      // Stall guard: the server gives a bridge up to 25 min for Veo and 15-30 min for Omni (30 at 4K), then
      // uploads and checks the clip; don't spin forever if an app-pool recycle orphaned the status entry mid-render.
      if (Date.now() - startedAt > 33 * 60 * 1000) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setLoading(false);
        setProgress(0);
        setError('This generation is taking unusually long — it may have failed on the server. Check History in a minute or try again.');
        fetchHistory(1);
        return;
      }
      pollStatus(pid);
    }, 1000);
  }, [pollStatus, fetchHistory]);

  // AI Director: Gemini Flash looks at the uploaded product images (and, when the
  // toggle is on, builds on the current prompt) and returns 3 distinct prompt
  // options tailored to the selected aspect ratio and duration.
  const handleAiDirector = async () => {
    if (images.length === 0) {
      setError('Add at least one product image first — the AI Director looks at it to write the prompt.');
      return;
    }
    setDirectorLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('jwtToken');
      const formData = new FormData();
      images.forEach(img => formData.append('images', img));
      formData.append('mode', videoMode);
      formData.append('idea', directorUseIdea ? prompt.trim() : '');
      formData.append('aspectRatio', aspectRatio);
      formData.append('duration', duration.toString());

      const response = await fetch(`${API_BASE_URL}/api/AIVideoGenerator/dictate-prompt`, {
        method: 'POST',
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
        body: formData,
      });
      const data = await response.json();
      if (response.ok && data.success && data.prompts?.length) {
        setDirectorPrompts(data.prompts);
        setDirectorSavedIdx([]);
        setDirectorSavingIdx(-1);
        setDirectorDialogOpen(true);
      } else {
        setError(data.message || data.error || 'AI Director could not write prompts. Please try again.');
      }
    } catch (err) {
      setError(`AI Director failed: ${err.message}`);
    } finally {
      setDirectorLoading(false);
    }
  };

  // Save one of the AI Director's options into the server-backed prompt library.
  const handleSaveDirectorPrompt = async (idx) => {
    const p = directorPrompts[idx];
    if (!p || directorSavedIdx.includes(idx)) return;
    setDirectorSavingIdx(idx);
    try {
      await apiCall(API_ENDPOINTS.AI_PROMPTS_CREATE, {
        method: 'POST',
        body: JSON.stringify({ name: p.name, text: p.text, mode: `Video-${videoMode}`, isPublic: false }),
      });
      setDirectorSavedIdx(prev => [...prev, idx]);
      fetchPrompts(videoMode);
    } catch (err) {
      enqueueSnackbar(`Could not save "${p.name}": ${err.message}`, { variant: 'error' });
    } finally {
      setDirectorSavingIdx(-1);
    }
  };

  // Fix It: send the failed generation + what went wrong; Gemini returns a
  // surgically revised prompt, which lands in the Generate tab ready to re-run.
  const handleFixIt = async () => {
    const item = historyItems[playingHistoryIndex];
    if (!item) return;
    setFixItLoading(true);
    try {
      // Omni conversational edit: revise the existing video in place instead of
      // rewriting the prompt and re-rolling from scratch.
      if (fixItEditInPlace && item.engine === 'omni' && item.omniInteractionId) {
        const instruction = [...fixItIssues, fixItNotes.trim()].filter(Boolean).join('. ');
        const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/history/${item.generationId || item.id}/edit-omni`, {
          method: 'POST',
          body: JSON.stringify({ instruction }),
        });
        if (data.processId) {
          setFixItOpen(false); setFixItIssues([]); setFixItNotes('');
          setPlayingHistoryIndex(-1); setPlayerPromptExpanded(false); setPlayerPromptCopied(false);
          setMainTab('generator');
          startPollingJob(data.processId, Date.now(), 'Editing video in place (Omni conversational edit)…');
        } else {
          enqueueSnackbar(data.error || data.message || 'Edit failed', { variant: 'error' });
        }
        return;
      }
      const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/refine-prompt`, {
        method: 'POST',
        body: JSON.stringify({
          generationId: item.generationId || item.id,
          issues: fixItIssues,
          notes: fixItNotes.trim(),
        }),
      });
      if (data.success && data.prompt) {
        setPrompt(data.prompt);
        setActivePromptName('Fix It revision');
        setFixItOpen(false); setFixItIssues([]); setFixItNotes('');
        setPlayingHistoryIndex(-1); setPlayerPromptExpanded(false); setPlayerPromptCopied(false);
        setMainTab('generator');
        enqueueSnackbar('Revised prompt loaded — attach the product images and generate', { variant: 'success', autoHideDuration: 4000 });
      } else {
        enqueueSnackbar(data.message || 'Fix It could not revise the prompt', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(`Fix It failed: ${err.message}`, { variant: 'error' });
    } finally {
      setFixItLoading(false);
    }
  };

  // Open the trim editor for a history item
  const openEditor = (item) => {
    const d = parseInt(item.duration, 10);
    const dur = !isNaN(d) && d > 0 ? d : 8;
    setEditorVideoDur(dur);
    setEditTrim([0, dur]);
    editorPreviewStopAt.current = null;
    setEditorItem(item);
  };

  // While dragging a trim handle, seek the preview to that handle so the user
  // sees exactly the frame where the clip will start/end.
  const handleTrimChange = (_, value, activeThumb) => {
    setEditTrim(value);
    const vid = editorVideoRef.current;
    if (vid && typeof activeThumb === 'number' && value[activeThumb] != null) {
      editorPreviewStopAt.current = null; // cancel any running trim preview
      if (!vid.paused) vid.pause();
      vid.currentTime = value[activeThumb];
    }
  };

  // Play just the trimmed range once, so the user can check the final clip.
  const handlePreviewTrim = () => {
    const vid = editorVideoRef.current;
    if (!vid) return;
    vid.currentTime = editTrim[0];
    editorPreviewStopAt.current = editTrim[1];
    vid.play().catch(() => {});
  };

  // Apply trim → server produces a new video that lands in History.
  const handleApplyEdit = async () => {
    if (!editorItem) return;
    setEditApplying(true);
    try {
      const [ts, te] = editTrim;
      const body = { generationId: editorItem.generationId || editorItem.id };
      if (ts > 0.05) body.trimStart = ts;
      if (te < editorVideoDur - 0.05) body.trimEnd = te;
      const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/edit`, { method: 'POST', body: JSON.stringify(body) });
      if (data.success) {
        setEditorItem(null);
        await fetchHistory(1);
        enqueueSnackbar('Edited video added to History', { variant: 'success', autoHideDuration: 3500 });
      } else {
        enqueueSnackbar(data.message || 'Edit failed', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(`Edit failed: ${err.message}`, { variant: 'error' });
    } finally {
      setEditApplying(false);
    }
  };

  // Merge the selected videos (in pick order) into one new History entry.
  const handleMergeVideos = async () => {
    if (mergeSel.length < 2) return;
    setMergeApplying(true);
    try {
      const ids = mergeSel.map(s => s.generationId || s.id);
      const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/merge`, { method: 'POST', body: JSON.stringify({ generationIds: ids }) });
      if (data.success) {
        setMergeMode(false); setMergeSel([]);
        await fetchHistory(1);
        enqueueSnackbar(`Merged ${ids.length} videos — added to History`, { variant: 'success', autoHideDuration: 3500 });
      } else {
        enqueueSnackbar(data.message || 'Merge failed', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(`Merge failed: ${err.message}`, { variant: 'error' });
    } finally {
      setMergeApplying(false);
    }
  };

  // Run the product-fidelity QC on the video currently open in the player.
  const handleCheckFidelity = async () => {
    const item = historyItems[playingHistoryIndex];
    if (!item) return;
    const id = item.generationId || item.id;
    setFidelityChecking(true);
    try {
      const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/history/${id}/fidelity`, { method: 'POST' });
      if (data.success) {
        setHistoryItems(prev => prev.map(it =>
          (it.generationId || it.id) === id
            ? { ...it, fidelityScore: data.fidelityScore, fidelityJson: data.fidelityJson }
            : it));
      } else {
        enqueueSnackbar(data.message || 'Fidelity check failed', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(`Fidelity check failed: ${err.message}`, { variant: 'error' });
    } finally {
      setFidelityChecking(false);
    }
  };

  // Veo scene extension: +7s conditioned on the final second of the clip.
  const handleExtendVideo = async () => {
    const item = historyItems[playingHistoryIndex];
    if (!item || !extendPrompt.trim()) return;
    setExtendLoading(true);
    try {
      const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/history/${item.generationId || item.id}/extend`, {
        method: 'POST',
        body: JSON.stringify({ prompt: extendPrompt.trim() }),
      });
      if (data.processId) {
        setExtendOpen(false); setExtendPrompt('');
        setPlayingHistoryIndex(-1); setPlayerPromptExpanded(false); setPlayerPromptCopied(false);
        setMainTab('generator');
        startPollingJob(data.processId, Date.now(), 'Extending video +7s (Veo scene extension)…');
      } else {
        enqueueSnackbar(data.error || data.message || 'Extension failed', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(`Extension failed: ${err.message}`, { variant: 'error' });
    } finally {
      setExtendLoading(false);
    }
  };

  // Generate video
  const handleGenerate = async () => {
    if (images.length === 0) {
      setError('Please add at least one reference image.');
      return;
    }
    if (!prompt.trim()) {
      setError('Please enter a prompt.');
      return;
    }
    if (engine === 'omni') {
      const totalMB = images.reduce((s, f) => s + (f.size || 0), 0) / 1048576;
      if (totalMB > 14) {
        setError(`Gemini Omni Flash sends images inline and is capped at ~14MB of reference images — yours total ${totalMB.toFixed(1)}MB. Remove or compress an image (or switch to Veo).`);
        return;
      }
    }
    if (isVeoEngine(engine) && images.length > 3) {
      setError(`Veo 3.1 accepts at most 3 reference images — you have ${images.length}. Remove ${images.length - 3} (or switch to Omni Flash).`);
      return;
    }
    if (engine === 'veo-fast' && resolution === '4k') {
      setError('Veo 3.1 Fast makes 720p or 1080p videos — pick 1080p, or Veo 3.1 for 4K.');
      return;
    }
    {
      const totalMB = images.reduce((s, f) => s + (f.size || 0), 0) / 1048576;
      if (totalMB > 95) {
        setError(`Uploads are capped at ~95MB in total — your photos total ${totalMB.toFixed(1)}MB. Remove or compress a photo.`);
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(0);
    setLogs([]);

    try {
      const token = localStorage.getItem('jwtToken');
      const formData = new FormData();
      images.forEach(img => formData.append('images', img));
      formData.append('prompt', prompt.trim());
      formData.append('aspectRatio', aspectRatio);
      formData.append('resolution', resolution);
      formData.append('duration', duration.toString());
      formData.append('engine', engine);
      if (isVeoEngine(engine)) {
        formData.append('interpolate', (interpolate && images.length === 2).toString());
        formData.append('firstFrame', (firstFrame && images.length === 1).toString());
        formData.append('audio', veoSound.toString());
      }

      const response = await fetch(`${API_BASE_URL}/api/AIVideoGenerator/generate`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = 'Generation request failed';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        } catch (parseErr) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const pid = data.processId;
      setLogs([`[${new Date().toLocaleTimeString()}] Video generation started...`]);
      // Snapshot the inputs of THIS generation (fresh blob URLs) for the compare view.
      startPollingJob(pid, Date.now(), null, images.map(f => ({ preview: URL.createObjectURL(f), name: f.name })));
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message || 'An error occurred during video generation.');
      setLoading(false);
    }
  };

  // Download video from result
  const handleDownload = async () => {
    if (results?.s3Url) {
      const link = document.createElement('a');
      link.href = results.s3Url;
      link.download = results.videoFileName || 'generated_video.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    if (!processId) return;
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AIVideoGenerator/download/${processId}`, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = results?.videoFileName || 'generated_video.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download video.');
    }
  };

  // Download from history
  const handleHistoryDownload = async (generationId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AIVideoGenerator/history/${generationId}/download`, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `video_${generationId}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('History download error:', err);
      setError('Failed to download video from history.');
    }
  };

  // Quick-fill prompt
  const handleQuickPrompt = (promptItem) => {
    setPrompt(promptItem.text);
    setActivePromptName(promptItem.name);
  };

  // Fetch server prompts for current video mode
  const fetchPrompts = useCallback(async (m) => {
    setPromptsLoading(true);
    try {
      const modeKey = `Video-${m || videoMode}`;
      const data = await apiCall(API_ENDPOINTS.AI_PROMPTS_LIST(modeKey));
      if (data.success) {
        setPrompts(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch prompts:', err);
    } finally {
      setPromptsLoading(false);
    }
  }, [videoMode]);

  // Load prompts on mount and when videoMode changes
  useEffect(() => {
    fetchPrompts(videoMode);
  }, [videoMode, fetchPrompts]);

  // Save new prompt
  const handleSavePrompt = async () => {
    if (!editPromptName.trim() || !editPromptText.trim()) return;
    setPromptSaveError('');
    try {
      if (editingPrompt) {
        await apiCall(API_ENDPOINTS.AI_PROMPTS_UPDATE(editingPrompt.id), {
          method: 'PUT',
          body: JSON.stringify({ name: editPromptName.trim(), text: editPromptText.trim(), isPublic: editPromptIsPublic })
        });
      } else {
        await apiCall(API_ENDPOINTS.AI_PROMPTS_CREATE, {
          method: 'POST',
          body: JSON.stringify({ name: editPromptName.trim(), text: editPromptText.trim(), mode: `Video-${videoMode}`, isPublic: editPromptIsPublic })
        });
      }
      setShowPromptDialog(false);
      setEditingPrompt(null);
      setEditPromptName('');
      setEditPromptText('');
      setEditPromptIsPublic(false);
      fetchPrompts();
    } catch (err) {
      console.error('Failed to save prompt:', err);
      setPromptSaveError(err?.message || 'Failed to save the prompt. Please try again.');
    }
  };

  // Delete prompt
  const handleDeletePrompt = async (id) => {
    try {
      await apiCall(API_ENDPOINTS.AI_PROMPTS_DELETE(id), { method: 'DELETE' });
      fetchPrompts();
    } catch (err) {
      console.error('Failed to delete prompt:', err);
    }
  };

  // Toggle prompt public/private
  const handleTogglePromptPublic = async (promptItem) => {
    try {
      await apiCall(API_ENDPOINTS.AI_PROMPTS_UPDATE(promptItem.id), {
        method: 'PUT',
        body: JSON.stringify({ isPublic: !promptItem.isPublic })
      });
      fetchPrompts();
    } catch (err) {
      console.error('Failed to toggle prompt visibility:', err);
    }
  };

  // Open dialog for new prompt
  const openNewPromptDialog = () => {
    setEditingPrompt(null);
    setEditPromptName('');
    setEditPromptText(prompt); // pre-fill with current prompt text
    setEditPromptIsPublic(false);
    setPromptSaveError('');
    setShowPromptDialog(true);
  };

  // Open dialog for editing existing prompt
  const openEditPromptDialog = (promptItem) => {
    setEditingPrompt(promptItem);
    setEditPromptName(promptItem.name);
    setEditPromptText(promptItem.text);
    setEditPromptIsPublic(promptItem.isPublic || false);
    setPromptSaveError('');
    setShowPromptDialog(true);
  };

  const isOmni = engine === 'omni';
  const isVeo = isVeoEngine(engine);
  // Veo (Google Cloud) makes 4, 6 or 8 s clips, but only 8 s when photos are used as references;
  // a literal first frame (or first + last frame) allows 4-8 s. Omni accepts any 3-10s.
  const veoFrameMode = (firstFrame && images.length === 1) || (interpolate && images.length === 2);
  const needsEightSeconds = isVeo && images.length > 0 && !veoFrameMode;
  const durationOptions = isOmni ? [4, 6, 8, 10] : [4, 6, 8];

  useEffect(() => {
    if (needsEightSeconds && duration !== 8) {
      setDuration(8);
    }
  }, [needsEightSeconds, duration]);

  const handleDurationChange = (_, val) => {
    if (val !== null && !needsEightSeconds) {
      setDuration(val);
    } else if (val === 8) {
      setDuration(8);
    }
  };

  const handleAspectRatioChange = (_, val) => {
    if (val !== null) setAspectRatio(val);
  };

  const handleEngineChange = (_, val) => {
    if (!val || val === engine) return;
    setEngine(val);
    // Keep the user's resolution/duration when the new engine supports them; otherwise
    // fall back to the nearest valid choice.
    const tiers = val === 'omni' ? ['360p', '720p', '1080p', '4k']
      : val === 'veo-fast' ? ['720p', '1080p']
      : ['720p', '1080p', '4k'];
    // Nearest valid tier: 4K -> 1080p on Veo Fast, 360p -> 720p on Veo.
    if (!tiers.includes(resolution)) setResolution(resolution === '4k' ? '1080p' : '720p');
    const maxDur = val === 'omni' ? 10 : 8;
    if (duration > maxDur) setDuration(maxDur);
  };

  const handleResolutionChange = (_, val) => {
    if (val !== null) setResolution(val);
  };

  const totalHistoryPages = Math.ceil(historyTotal / historyPageSize);

  // ==================== COLLECTION HELPERS ====================
  const currentCollection = collections.find(c => c.id === selectedCollection) || null;

  // Instagram-style save state: filled bookmark when the video is in any collection
  const isVideoInCollection = (genId) =>
    genId != null && collections.some(c => (c.videos || []).some(v => v.generationId === genId));

  // Toggle the current save-menu video in/out of a collection (menu stays open so
  // users can save to several collections in one go, Instagram-style).
  const toggleVideoInCollection = (col) => {
    const vid = saveMenuVideoData;
    if (!vid || vid.generationId == null) return;
    const inCol = (col.videos || []).some(v => v.generationId === vid.generationId);
    if (inCol) {
      setCollections(prev => prev.map(c => c.id === col.id
        ? { ...c, videos: (c.videos || []).filter(v => v.generationId !== vid.generationId) }
        : c));
    } else {
      setCollections(prev => prev.map(c => c.id === col.id
        ? {
            ...c,
            videos: [...(c.videos || []), {
              id: Date.now().toString(),
              generationId: vid.generationId,
              prompt: vid.prompt || '',
              s3Url: vid.s3Url || '',
              videoFileName: vid.videoFileName || '',
              duration: vid.duration || '',
              aspectRatio: vid.aspectRatio || '',
              addedAt: new Date().toISOString(),
            }],
          }
        : c));
    }
  };

  // Create a collection from inside the save menu and drop the current video straight into it
  const createCollectionAndAddCurrent = () => {
    const name = newColNameInMenu.trim();
    if (!name) return;
    const vid = saveMenuVideoData;
    const newCol = {
      id: Date.now().toString(),
      name,
      createdAt: new Date().toISOString(),
      videos: vid && vid.generationId != null ? [{
        id: (Date.now() + 1).toString(),
        generationId: vid.generationId,
        prompt: vid.prompt || '',
        s3Url: vid.s3Url || '',
        videoFileName: vid.videoFileName || '',
        duration: vid.duration || '',
        aspectRatio: vid.aspectRatio || '',
        addedAt: new Date().toISOString(),
      }] : [],
    };
    setCollections(prev => [...prev, newCol]);
    enqueueSnackbar(`Created "${name}" and saved the video`, { variant: 'success', autoHideDuration: 2500 });
    setNewColNameInMenu('');
    setShowCreateInMenu(false);
  };

  const createCollection = (name) => {
    if (!name?.trim()) return;
    const newCol = {
      id: Date.now().toString(),
      name: name.trim(),
      createdAt: new Date().toISOString(),
      videos: [],
    };
    setCollections(prev => [...prev, newCol]);
    setSelectedCollection(newCol.id);
    setNewCollectionName('');
    setShowCreateCollection(false);
  };

  const renameCollection = (id, name) => {
    if (!name?.trim()) return;
    setCollections(prev => prev.map(c => c.id === id ? { ...c, name: name.trim() } : c));
    setEditingCollectionId(null);
    setEditingCollectionName('');
  };

  const deleteCollection = (id) => {
    setCollections(prev => prev.filter(c => c.id !== id));
    if (selectedCollection === id) setSelectedCollection(null);
    setDeleteConfirmOpen(false);
    setDeleteConfirmId(null);
  };

  const confirmDeleteCollection = (id) => {
    setDeleteConfirmId(id);
    setDeleteConfirmOpen(true);
  };

  const addToCollection = (collectionId, videoData) => {
    setCollections(prev => prev.map(c => {
      if (c.id !== collectionId) return c;
      // Avoid duplicates by generationId
      if (c.videos.some(v => v.generationId === videoData.generationId)) return c;
      return {
        ...c,
        videos: [...c.videos, {
          id: Date.now().toString(),
          generationId: videoData.generationId,
          prompt: videoData.prompt || '',
          s3Url: videoData.s3Url || '',
          videoFileName: videoData.videoFileName || '',
          duration: videoData.duration || '',
          aspectRatio: videoData.aspectRatio || '',
          addedAt: new Date().toISOString(),
        }],
      };
    }));
    handleCloseSaveMenu();
  };

  const removeFromCollection = (collectionId, videoIdx) => {
    setCollections(prev => prev.map(c => {
      if (c.id !== collectionId) return c;
      return { ...c, videos: c.videos.filter((_, i) => i !== videoIdx) };
    }));
  };

  // Save to collection menu
  const handleOpenSaveMenu = (event, videoData) => {
    event.stopPropagation();
    setSaveMenuAnchor(event.currentTarget);
    setSaveMenuVideoData(videoData);
  };

  const handleCloseSaveMenu = () => {
    setSaveMenuAnchor(null);
    setSaveMenuVideoData(null);
  };

  // Add videos dialog — 9 per page for a clean 3x3 grid
  const openAddVideosDialog = async () => {
    setAddVideosDialogOpen(true);
    setAddVideosLoading(true);
    setAddVideosPreviewId(null);
    try {
      const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/history?page=1&pageSize=9`);
      setAddVideosItems(data.generations || data.items || []);
      setAddVideosTotalPages(Math.ceil((data.totalItems || data.totalCount || 0) / 9));
      setAddVideosPage(1);
    } catch (err) {
      console.error('Failed to load videos for add dialog:', err);
    } finally {
      setAddVideosLoading(false);
    }
  };

  const fetchAddVideosPage = async (page) => {
    setAddVideosLoading(true);
    setAddVideosPreviewId(null);
    try {
      const data = await apiCall(`${API_BASE_URL}/api/AIVideoGenerator/history?page=${page}&pageSize=9`);
      setAddVideosItems(data.generations || data.items || []);
      setAddVideosTotalPages(Math.ceil((data.totalItems || data.totalCount || 0) / 9));
      setAddVideosPage(page);
    } catch (err) {
      console.error('Failed to load videos:', err);
    } finally {
      setAddVideosLoading(false);
    }
  };

  // Comparison images
  const comparisonVideos = currentCollection
    ? currentCollection.videos.filter((_, idx) => collectionCompareSelected.has(idx))
    : [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto', position: 'relative' }}>
        <InterfaceHeader
          title="AI VIDEO GENERATOR"
          color={AI_COLOR}
          category="ai"
        />

        {/* Main Tab Toggle: Generator / History / Collections */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem' }, '& .MuiTabs-indicator': { bgcolor: AI_COLOR } }}>
            <Tab icon={<Videocam sx={{ fontSize: 18 }} />} iconPosition="start" label="Generator" value="generator" />
            <Tab icon={<FaHistory size={14} />} iconPosition="start" label="History" value="history" />
            <Tab icon={<MdFolderSpecial size={16} />} iconPosition="start" label="Collections" value="collections" />
          </Tabs>
        </Box>

        {/* ==================== GENERATOR TAB ==================== */}
        {mainTab === 'generator' && (
          <Grid container spacing={3}>
            {/* ===== LEFT COLUMN (8 cols) ===== */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>

                {/* AI Model Selection */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Videocam sx={{ color: AI_COLOR, fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>AI Model</Typography>
                  </Box>
                  <ToggleButtonGroup
                    value={engine}
                    exclusive
                    onChange={handleEngineChange}
                    fullWidth
                    disabled={loading}
                    sx={{
                      gap: 1.5,
                      '& .MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.divider} !important`,
                        borderRadius: '10px !important',
                      },
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        px: 1.75,
                        py: 1.25,
                        '&.Mui-selected': {
                          bgcolor: alpha(AI_COLOR, 0.1),
                          borderColor: `${AI_COLOR} !important`,
                          '&:hover': { bgcolor: alpha(AI_COLOR, 0.16) },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="veo">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: engine === 'veo' ? AI_COLOR : theme.palette.text.primary }}>
                          Veo 3.1
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', lineHeight: 1.3, mt: 0.3 }}>
                          Cinematic · up to 4K · 4–8s
                        </Typography>
                      </Box>
                    </ToggleButton>
                    <ToggleButton value="veo-fast">
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: engine === 'veo-fast' ? AI_COLOR : theme.palette.text.primary }}>
                            Veo 3.1 Fast
                          </Typography>
                          <Chip label="New" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: alpha(AI_COLOR, 0.15), color: AI_COLOR }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', lineHeight: 1.3, mt: 0.3 }}>
                          Cheaper drafts · up to 1080p · 4–8s
                        </Typography>
                      </Box>
                    </ToggleButton>
                    <ToggleButton value="omni">
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: engine === 'omni' ? AI_COLOR : theme.palette.text.primary }}>
                            Gemini Omni Flash
                          </Typography>
                          <Chip label="New" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: alpha(AI_COLOR, 0.15), color: AI_COLOR }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', lineHeight: 1.3, mt: 0.3 }}>
                          Fast · up to 4K + audio · 3–10s
                        </Typography>
                      </Box>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* Reference Images Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FaImages color={AI_COLOR} size={18} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Reference Images
                    </Typography>
                    <Chip
                      label={`${images.length}/${maxImages}`}
                      size="small"
                      sx={{
                        bgcolor: alpha(AI_COLOR, 0.1),
                        color: AI_COLOR,
                        fontWeight: 600,
                        fontSize: 11,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                    Upload up to {maxImages} product images. These reference images preserve the exact product appearance in the generated video.
                  </Typography>

                  {/* Dropzone */}
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: `2px dashed ${isDragActive ? AI_COLOR : images.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragActive
                        ? alpha(AI_COLOR, 0.08)
                        : images.length > 0
                          ? (isDark ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 175, 80, 0.04)')
                          : 'transparent',
                      cursor: loading ? 'default' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading ? 0.6 : 1,
                      '&:hover': {
                        borderColor: loading ? undefined : AI_COLOR,
                        bgcolor: loading ? undefined : alpha(AI_COLOR, 0.06),
                      },
                    }}
                  >
                    <input {...getInputProps()} />
                    {images.length === 0 ? (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragActive ? AI_COLOR : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {isDragActive ? 'Drop images here' : 'Drop product images here or click to browse'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          JPG, PNG, WebP, TIFF supported (max {maxImages} images{engine === 'omni' ? ' \u00b7 14MB total' : ''})
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 36, color: '#4CAF50', mb: 0.5 }} />
                        <Typography variant="body2">
                          {images.length} image{images.length !== 1 ? 's' : ''} selected
                          {images.length < 3 ? ' - drop more or click to add' : ''}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Thumbnails */}
                  {images.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                      {images.map((img, idx) => (
                        <Box
                          key={idx}
                          onClick={() => openImagePreview(images.map(i => ({ src: i.preview, label: i.name })), idx)}
                          sx={{
                            position: 'relative',
                            width: 100,
                            height: 100,
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: `1px solid ${theme.palette.divider}`,
                            cursor: 'pointer',
                            '&:hover': { borderColor: AI_COLOR },
                          }}
                        >
                          <img
                            src={img.preview}
                            alt={`Reference ${idx + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: '#fff',
                              p: 0.3,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                            }}
                          >
                            <Clear sx={{ fontSize: 14 }} />
                          </IconButton>
                          <Typography
                            variant="caption"
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              bgcolor: 'rgba(0,0,0,0.5)',
                              color: '#fff',
                              textAlign: 'center',
                              fontSize: 10,
                              py: 0.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              px: 0.5,
                            }}
                          >
                            {img.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {/* Over the Omni count limit */}
                  {engine === 'omni' && images.length > 10 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#f44336', fontWeight: 700 }}>
                      Omni Flash accepts at most 10 reference images — remove {images.length - 10}.
                    </Typography>
                  )}
                  {refTotalMB > 95 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#f44336', fontWeight: 700 }}>
                      Photos total {refTotalMB.toFixed(1)}MB — uploads are capped at ~95MB. Remove or compress a photo.
                    </Typography>
                  )}
                  {/* Over the Veo count limit (e.g. after switching from Omni) */}
                  {isVeo && images.length > 3 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#f44336', fontWeight: 700 }}>
                      Veo 3.1 accepts at most 3 reference images — remove {images.length - 3} or switch to Omni Flash.
                    </Typography>
                  )}
                  {/* Omni sends images inline in a size-capped request — show the
                      running total so the user knows what fits before generating. */}
                  {engine === 'omni' && images.length > 0 && (() => {
                    const totalMB = images.reduce((s, f) => s + (f.size || 0), 0) / 1048576;
                    const over = totalMB > 14;
                    return (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: over ? '#f44336' : 'text.secondary', fontWeight: over ? 700 : 400 }}>
                        Reference images: {totalMB.toFixed(1)}MB / 14MB Omni Flash limit{over ? ' — remove or compress an image' : ''}
                      </Typography>
                    );
                  })()}
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* Video Settings Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Videocam sx={{ color: AI_COLOR, fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Video Settings
                    </Typography>
                  </Box>

                  <Grid container spacing={2.5}>
                    {/* Aspect Ratio */}
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Aspect Ratio
                      </Typography>
                      <ToggleButtonGroup
                        value={aspectRatio}
                        exclusive
                        onChange={handleAspectRatioChange}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontSize: 12,
                            py: 0.8,
                            '&.Mui-selected': {
                              bgcolor: alpha(AI_COLOR, 0.15),
                              color: AI_COLOR,
                              borderColor: AI_COLOR,
                              fontWeight: 600,
                              '&:hover': { bgcolor: alpha(AI_COLOR, 0.25) },
                            },
                          },
                        }}
                      >
                        <ToggleButton value="16:9">16:9 Landscape</ToggleButton>
                        <ToggleButton value="9:16">9:16 Portrait</ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>

                    {/* Duration */}
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Duration
                      </Typography>
                      <ToggleButtonGroup
                        value={duration}
                        exclusive
                        onChange={handleDurationChange}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontSize: 12,
                            py: 0.8,
                            '&.Mui-selected': {
                              bgcolor: alpha(AI_COLOR, 0.15),
                              color: AI_COLOR,
                              borderColor: AI_COLOR,
                              fontWeight: 600,
                              '&:hover': { bgcolor: alpha(AI_COLOR, 0.25) },
                            },
                            '&.Mui-disabled': {
                              opacity: 0.4,
                            },
                          },
                        }}
                      >
                        {durationOptions.map((d) => (
                          <ToggleButton key={d} value={d} disabled={needsEightSeconds && d !== 8}>{d}s</ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Grid>

                    {/* Resolution */}
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Resolution
                      </Typography>
                      {(
                        <ToggleButtonGroup
                          value={resolution}
                          exclusive
                          onChange={handleResolutionChange}
                          size="small"
                          fullWidth
                          sx={{
                            '& .MuiToggleButton-root': {
                              textTransform: 'none',
                              fontSize: 12,
                              py: 0.8,
                              '&.Mui-selected': {
                                bgcolor: alpha(AI_COLOR, 0.15),
                                color: AI_COLOR,
                                borderColor: AI_COLOR,
                                fontWeight: 600,
                                '&:hover': { bgcolor: alpha(AI_COLOR, 0.25) },
                              },
                            },
                          }}
                        >
                          {/* Omni 1.1 adds a 360p draft tier; Veo 3.1 Fast stops at 1080p. */}
                          {isOmni && <ToggleButton value="360p">360p</ToggleButton>}
                          <ToggleButton value="720p">720p</ToggleButton>
                          <ToggleButton value="1080p">1080p</ToggleButton>
                          {engine !== 'veo-fast' && <ToggleButton value="4k">4K</ToggleButton>}
                        </ToggleButtonGroup>
                      )}
                    </Grid>
                  </Grid>

                  {needsEightSeconds && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                      Note: Veo makes 8-second clips when photos are used as references. For a 4–6s clip, use one photo
                      as the literal first frame (or two photos as first and last frame).
                    </Typography>
                  )}
                  {engine === 'veo-fast' && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                      Note: Veo 3.1 Fast is the cheaper Veo tier for drafts (about a quarter of the price), at 720p or 1080p.
                    </Typography>
                  )}
                  {isOmni && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                      Note: Gemini Omni 1.1 Flash renders 360p / 720p / 1080p / 4K clips (3–10s) with audio,
                      and is roughly half the price of Veo at 4K. 4K takes about 2 minutes per clip.
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* Prompt Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FaVideo color={AI_COLOR} size={16} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Prompt
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" variant={videoMode === 'Furniture' ? 'contained' : 'outlined'}
                        startIcon={<FaChair size={12} />}
                        onClick={() => { setVideoMode('Furniture'); setPrompt(DEFAULT_PROMPTS_FURNITURE[0].text); setActivePromptName(DEFAULT_PROMPTS_FURNITURE[0].name); }}
                        sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.3, px: 1.5,
                          bgcolor: videoMode === 'Furniture' ? AI_COLOR : 'transparent',
                          borderColor: AI_COLOR, color: videoMode === 'Furniture' ? 'white' : theme.palette.text.primary,
                          '&:hover': { bgcolor: videoMode === 'Furniture' ? TOOL_COLORS.AI.hover : `${AI_COLOR}10` } }}>
                        Furniture
                      </Button>
                      <Button size="small" variant={videoMode === 'Rugs' ? 'contained' : 'outlined'}
                        startIcon={<FaBorderAll size={12} />}
                        onClick={() => { setVideoMode('Rugs'); setPrompt(DEFAULT_PROMPTS_RUGS[0].text); setActivePromptName(DEFAULT_PROMPTS_RUGS[0].name); }}
                        sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.3, px: 1.5,
                          bgcolor: videoMode === 'Rugs' ? AI_COLOR : 'transparent',
                          borderColor: AI_COLOR, color: videoMode === 'Rugs' ? 'white' : theme.palette.text.primary,
                          '&:hover': { bgcolor: videoMode === 'Rugs' ? TOOL_COLORS.AI.hover : `${AI_COLOR}10` } }}>
                        Rugs
                      </Button>
                    </Box>
                  </Box>

                  {/* Quick-fill buttons */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'nowrap', overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: alpha(AI_COLOR, 0.3), borderRadius: 2 } }}>
                    {DEFAULT_PROMPTS.map((p) => (
                      <Chip
                        key={p.name}
                        label={p.name}
                        size="small"
                        onClick={() => handleQuickPrompt(p)}
                        variant={activePromptName === p.name ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          flexShrink: 0,
                          fontWeight: activePromptName === p.name ? 600 : 400,
                          bgcolor: activePromptName === p.name ? alpha(AI_COLOR, 0.15) : 'transparent',
                          color: activePromptName === p.name ? AI_COLOR : theme.palette.text.primary,
                          borderColor: activePromptName === p.name ? AI_COLOR : theme.palette.divider,
                          '&:hover': {
                            bgcolor: alpha(AI_COLOR, 0.1),
                            borderColor: AI_COLOR,
                          },
                        }}
                      />
                    ))}
                    {prompts.map((p) => (
                      <Chip
                        key={`server-${p.id}`}
                        label={p.name}
                        size="small"
                        onClick={() => handleQuickPrompt(p)}
                        variant={activePromptName === p.name ? 'filled' : 'outlined'}
                        icon={p.isPublic ? <FaGlobe size={8} /> : undefined}
                        sx={{
                          cursor: 'pointer',
                          flexShrink: 0,
                          fontWeight: activePromptName === p.name ? 600 : 400,
                          bgcolor: activePromptName === p.name ? alpha(AI_COLOR, 0.15) : 'transparent',
                          color: activePromptName === p.name ? AI_COLOR : theme.palette.text.primary,
                          borderColor: activePromptName === p.name ? AI_COLOR : alpha(AI_COLOR, 0.4),
                          '&:hover': {
                            bgcolor: alpha(AI_COLOR, 0.1),
                            borderColor: AI_COLOR,
                          },
                        }}
                      />
                    ))}
                  </Box>

                  {/* Prompt Library Toggle */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => setShowPromptLibrary(!showPromptLibrary)}
                        startIcon={<FaBookmark size={12} />}
                        sx={{
                          textTransform: 'none',
                          color: AI_COLOR,
                          fontSize: 12,
                          fontWeight: 500,
                          '&:hover': { bgcolor: alpha(AI_COLOR, 0.08) },
                        }}
                      >
                        {showPromptLibrary ? 'Hide Prompt Library' : 'Prompt Library'}
                      </Button>
                      <Button
                        size="small"
                        onClick={openNewPromptDialog}
                        startIcon={<FaPlus size={10} />}
                        sx={{
                          textTransform: 'none',
                          color: AI_COLOR,
                          fontSize: 12,
                          fontWeight: 500,
                          '&:hover': { bgcolor: alpha(AI_COLOR, 0.08) },
                        }}
                      >
                        Add New Prompt
                      </Button>
                    </Box>

                    {showPromptLibrary && (
                      <Box sx={{
                        mt: 1,
                        p: 2,
                        border: `1px solid ${alpha(AI_COLOR, 0.3)}`,
                        borderRadius: 1.5,
                        bgcolor: alpha(AI_COLOR, 0.03),
                      }}>
                        {/* Built-in Prompts */}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                          Built-in Prompts
                        </Typography>
                        {DEFAULT_PROMPTS.map((p) => (
                          <Box
                            key={`lib-${p.name}`}
                            onClick={() => handleQuickPrompt(p)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              py: 0.8,
                              px: 1.5,
                              mb: 0.5,
                              borderRadius: 1,
                              cursor: 'pointer',
                              bgcolor: activePromptName === p.name ? alpha(AI_COLOR, 0.1) : 'transparent',
                              '&:hover': { bgcolor: alpha(AI_COLOR, 0.06) },
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontSize: 12, fontWeight: activePromptName === p.name ? 600 : 400 }}>
                                {p.name}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: 10, color: theme.palette.text.secondary, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {p.text.substring(0, 80)}...
                              </Typography>
                            </Box>
                            <Chip label="Built-in" size="small" sx={{ height: 18, fontSize: 9, bgcolor: alpha(AI_COLOR, 0.1), color: AI_COLOR, ml: 1 }} />
                          </Box>
                        ))}

                        {/* Server Prompts */}
                        {prompts.length > 0 && (
                          <>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, display: 'block', mt: 1.5, mb: 0.5 }}>
                              Saved Prompts
                            </Typography>
                            {prompts.map((p) => (
                              <Box
                                key={`server-lib-${p.id}`}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  py: 0.8,
                                  px: 1.5,
                                  mb: 0.5,
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  bgcolor: activePromptName === p.name ? alpha(AI_COLOR, 0.1) : 'transparent',
                                  '&:hover': { bgcolor: alpha(AI_COLOR, 0.06) },
                                }}
                                onClick={() => handleQuickPrompt(p)}
                              >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: 12, fontWeight: activePromptName === p.name ? 600 : 400 }}>
                                      {p.name}
                                    </Typography>
                                    {p.isPublic && (
                                      <Chip icon={<FaGlobe size={8} />} label="Shared" size="small"
                                        sx={{ height: 18, fontSize: 9, bgcolor: alpha('#4caf50', 0.1), color: '#4caf50', '& .MuiChip-icon': { color: '#4caf50', ml: 0.5 } }} />
                                    )}
                                  </Box>
                                  <Typography variant="caption" sx={{ fontSize: 10, color: theme.palette.text.secondary, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {p.text.substring(0, 80)}...
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                                  <Tooltip title={p.isPublic ? 'Make Private' : 'Make Public'}>
                                    <Switch
                                      size="small"
                                      checked={p.isPublic || false}
                                      onClick={(e) => { e.stopPropagation(); handleTogglePromptPublic(p); }}
                                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4caf50' } }}
                                    />
                                  </Tooltip>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); openEditPromptDialog(p); }}
                                    sx={{ color: theme.palette.text.secondary, '&:hover': { color: AI_COLOR }, p: 0.3 }}
                                  >
                                    <FaEdit size={10} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); handleDeletePrompt(p.id); }}
                                    sx={{ color: theme.palette.text.secondary, '&:hover': { color: '#f44336' }, p: 0.3 }}
                                  >
                                    <FaTrash size={10} />
                                  </IconButton>
                                </Box>
                              </Box>
                            ))}
                          </>
                        )}

                        {prompts.length === 0 && !promptsLoading && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mt: 1.5, fontStyle: 'italic' }}>
                            No saved prompts yet. Click "Add New Prompt" to save prompts for reuse.
                          </Typography>
                        )}

                        {promptsLoading && (
                          <LinearProgress sx={{ mt: 1, '& .MuiLinearProgress-bar': { bgcolor: AI_COLOR } }} />
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* AI Director — Gemini sees the uploaded product images (and,
                      when the toggle is on, the current prompt) and returns 3
                      cinematic prompt options to save or use. */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                    <Tooltip title="On: the AI reads your current prompt and builds on top of it. Off: it works from the images alone.">
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => setDirectorUseIdea(v => !v)}>
                        <Switch size="small" checked={directorUseIdea} onChange={(e) => setDirectorUseIdea(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AI_COLOR },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: AI_COLOR } }} />
                        <Typography variant="caption" sx={{ color: directorUseIdea ? AI_TEXT : 'text.secondary', userSelect: 'none' }}>
                          Build on current prompt
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Tooltip title={images.length === 0
                      ? 'Add product image(s) first — the AI looks at them to write the prompts'
                      : `Gemini studies your product image${images.length > 1 ? 's' : ''} and writes 3 cinematic prompt options for a ${duration}s ${aspectRatio} video`}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAiDirector}
                          disabled={directorLoading || images.length === 0}
                          startIcon={directorLoading
                            ? <CircularProgress size={14} sx={{ color: AI_COLOR }} />
                            : <AutoAwesome sx={{ fontSize: 16 }} />}
                          sx={{ textTransform: 'none', borderColor: AI_COLOR, color: AI_COLOR,
                            '&:hover': { borderColor: AI_COLOR, bgcolor: alpha(AI_COLOR, 0.08) } }}
                        >
                          {directorLoading ? 'Directing…' : 'AI Director'}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      multiline
                      rows={4}
                      fullWidth
                      value={prompt}
                      onChange={(e) => { setPrompt(e.target.value); setActivePromptName(''); }}
                      placeholder="Describe the video you want to generate..."
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: 13,
                          // keep text clear of the floating copy button
                          '& textarea': { pr: 4.5 },
                          '&.Mui-focused fieldset': {
                            borderColor: AI_COLOR,
                          },
                        },
                      }}
                    />
                    {prompt.trim() && (
                      <Tooltip title={mainPromptCopied ? 'Copied!' : 'Copy prompt'}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(prompt).then(() => {
                              if (mainPromptCopiedTimer.current) clearTimeout(mainPromptCopiedTimer.current);
                              setMainPromptCopied(true);
                              mainPromptCopiedTimer.current = setTimeout(() => setMainPromptCopied(false), 1600);
                            }).catch(() => {});
                          }}
                          sx={{
                            position: 'absolute', top: 6, right: 6,
                            color: mainPromptCopied ? '#66bb6a' : 'text.secondary',
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            '&:hover': { color: mainPromptCopied ? '#66bb6a' : 'text.primary', bgcolor: 'background.paper' },
                          }}
                        >
                          {mainPromptCopied ? <Check sx={{ fontSize: 14 }} /> : <ContentCopy sx={{ fontSize: 13 }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* Veo extras: first/last-frame modes and sound */}
                {isVeo && images.length === 1 && (
                  <Tooltip title="Image-to-video: your photo becomes the literal first frame, so the product opens pixel-perfect (a stronger guarantee than reference images). Veo then animates from it. Allows 4, 6 or 8 second clips.">
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, cursor: 'pointer' }}
                      onClick={() => setFirstFrame(v => !v)}>
                      <Switch size="small" checked={firstFrame}
                        onChange={(e) => setFirstFrame(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AI_COLOR },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: AI_COLOR } }} />
                      <Typography variant="caption" sx={{ color: firstFrame ? AI_TEXT : 'text.secondary', userSelect: 'none' }}>
                        Use photo as the literal first frame (pixel-perfect product opening)
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
                {isVeo && images.length === 2 && (
                  <Tooltip title="Veo animates the transition between your two stills: image 1 is the literal first frame, image 2 the literal last frame. Perfect for before/after room reveals. Allows 4, 6 or 8 second clips.">
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, cursor: 'pointer' }}
                      onClick={() => setInterpolate(v => !v)}>
                      <Switch size="small" checked={interpolate}
                        onChange={(e) => setInterpolate(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AI_COLOR },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: AI_COLOR } }} />
                      <Typography variant="caption" sx={{ color: interpolate ? AI_TEXT : 'text.secondary', userSelect: 'none' }}>
                        First → last frame transition (image 1 = start, image 2 = end)
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
                {isVeo && (
                  <Tooltip title={`Veo can add a generated soundtrack (ambience, music, effects). A silent clip costs less: ~$${estimateVideoCost(engine, resolution, duration, false).toFixed(2)} instead of ~$${estimateVideoCost(engine, resolution, duration, true).toFixed(2)} for this one.`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, cursor: 'pointer' }}
                      onClick={() => setVeoSound(v => !v)}>
                      <Switch size="small" checked={veoSound}
                        onChange={(e) => setVeoSound(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AI_COLOR },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: AI_COLOR } }} />
                      <Typography variant="caption" sx={{ color: veoSound ? AI_TEXT : 'text.secondary', userSelect: 'none' }}>
                        Generate sound {veoSound ? '' : '(off — silent clips cost less)'}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}

                {/* Live cost meter — visible estimate before spending real money */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                  <Tooltip title="Estimated Google API cost for one generation at the current engine, duration and settings. Ballpark from published per-second rates — actual billing may differ.">
                    <Typography variant="caption" sx={{ fontWeight: 700, color: AI_TEXT, cursor: 'help' }}>
                      Est. cost ~${estimateVideoCost(engine, resolution, duration, isVeo ? veoSound : true).toFixed(2)}
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {' '}— {duration}s · {engineLabel(engine)} · {resolution.toUpperCase()} · {aspectRatio}{isVeo ? (veoSound ? ' · sound' : ' · silent') : ''}
                      </Typography>
                    </Typography>
                  </Tooltip>
                  {engine === 'veo' && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      draft on {resolution === '4k' ? 'Omni 1.1 Flash' : 'Veo 3.1 Fast'} ≈ ${(resolution === '4k'
                        ? estimateVideoCost('omni', resolution, Math.min(Math.max(duration, 3), 10))
                        : estimateVideoCost('veo-fast', resolution, duration, veoSound)).toFixed(2)}
                    </Typography>
                  )}
                </Box>
                {/* Generate Button */}
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleGenerate}
                  disabled={loading || images.length === 0 || overImageLimit}
                  startIcon={<FaPlay />}
                  sx={{
                    bgcolor: AI_COLOR,
                    '&:hover': { bgcolor: TOOL_COLORS.AI.hover },
                    '&.Mui-disabled': {
                      bgcolor: alpha(AI_COLOR, 0.3),
                      color: alpha('#fff', 0.6),
                    },
                    py: 1.5,
                    fontSize: 15,
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {loading ? 'Generating...' : 'Generate Video'}
                </Button>

                {/* Progress Section */}
                {loading && (
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Generating video...
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {Math.round(progress)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant={progress > 0 ? 'determinate' : 'indeterminate'}
                      value={progress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: isDark ? alpha(AI_COLOR, 0.2) : alpha(AI_COLOR, 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: AI_COLOR,
                          borderRadius: 4,
                        },
                      }}
                    />

                  </Box>
                )}

                {/* Error */}
                {error && (
                  <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                {/* Result Section */}
                {results && (
                  <Box sx={{ mt: 3 }}>
                    {/* View toggle: single video vs side-by-side comparison with the inputs */}
                    {results.s3Url && resultInputs.length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Button
                          size="small"
                          startIcon={<Fullscreen sx={{ fontSize: 18 }} />}
                          onClick={() => { setCompareDialogItem({ s3Url: results.s3Url, inputImages: resultInputs.map(i => i.preview) }); setZoomImageUrl(null); }}
                          sx={{ textTransform: 'none', fontSize: 12, color: AI_TEXT, '&:hover': { bgcolor: alpha(AI_COLOR, 0.08) } }}
                        >
                          Fullscreen compare
                        </Button>
                        <ToggleButtonGroup
                          value={compareMode ? 'compare' : 'video'}
                          exclusive
                          size="small"
                          onChange={(_, v) => { if (v) setCompareMode(v === 'compare'); }}
                          sx={{
                            '& .MuiToggleButton-root': {
                              textTransform: 'none', fontSize: 12, py: 0.4, px: 1.5,
                              '&.Mui-selected': { bgcolor: alpha(AI_COLOR, 0.15), color: AI_TEXT, borderColor: AI_COLOR, fontWeight: 600 },
                            },
                          }}
                        >
                          <ToggleButton value="video">Video</ToggleButton>
                          <ToggleButton value="compare">Compare with input</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    )}

                    {results.s3Url && compareMode && resultInputs.length > 0 ? (
                      /* Side-by-side: reference input(s) vs generated video, for spotting product alterations */
                      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.7 }}>
                            <FaImages size={12} color={theme.palette.text.secondary} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Reference Input {resultInputs.length > 1 ? `(${resultInputs.length})` : ''}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {resultInputs.map((img, idx) => (
                              <Box key={idx}
                                onClick={() => openImagePreview(resultInputs.map(i => ({ src: i.preview, label: i.name })), idx)}
                                sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}`, bgcolor: isDark ? '#111' : '#fafafa', cursor: 'pointer', '&:hover': { borderColor: AI_COLOR } }}>
                                <img src={img.preview} alt={`Reference ${idx + 1}`} style={{ width: '100%', display: 'block', maxHeight: 500, objectFit: 'contain' }} />
                                {img.name && (
                                  <Typography variant="caption" noWrap sx={{ display: 'block', px: 1, py: 0.5, color: theme.palette.text.secondary, fontSize: 10.5 }}>
                                    {img.name}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.7 }}>
                            <Videocam sx={{ fontSize: 15, color: AI_TEXT }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: AI_TEXT, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Generated Video
                            </Typography>
                          </Box>
                          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}`, bgcolor: '#000', position: 'sticky', top: 16 }}>
                            <video controls autoPlay loop muted playsInline style={{ width: '100%', display: 'block', maxHeight: 500 }} src={results.s3Url}>
                              Your browser does not support the video tag.
                            </video>
                          </Box>
                        </Box>
                      </Box>
                    ) : results.s3Url && (
                      <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}`, bgcolor: '#000', mb: 2 }}>
                        <video controls autoPlay style={{ width: '100%', display: 'block', maxHeight: 500 }} src={results.s3Url}>
                          Your browser does not support the video tag.
                        </video>
                      </Box>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleDownload}
                        sx={{
                          background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 16px rgba(46,125,50,0.4)',
                          },
                          transition: 'transform 150ms ease, box-shadow 150ms ease',
                          py: 1, px: 2, textTransform: 'none', borderRadius: 2,
                          justifyContent: 'flex-start',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, width: '100%' }}>
                          <Box sx={{
                            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: 'rgba(255,255,255,0.18)',
                          }}>
                            <FaDownload size={14} />
                          </Box>
                          <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.25 }}>
                              Download Video
                            </Typography>
                            <Typography noWrap sx={{ fontSize: 11, opacity: 0.85, lineHeight: 1.25 }}>
                              {(results.videoFileName || 'video.mp4')} · {resolution} · {duration}s · {aspectRatio}
                            </Typography>
                          </Box>
                        </Box>
                      </Button>
                      <Tooltip title={isVideoInCollection(results.generationId) ? 'Saved to Collection' : 'Save to Collection'}>
                        <IconButton
                          onClick={(e) => handleOpenSaveMenu(e, {
                            generationId: results.generationId,
                            prompt: prompt,
                            s3Url: results.s3Url,
                            videoFileName: results.videoFileName,
                            duration: `${duration}s`,
                            aspectRatio: aspectRatio,
                          })}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            px: 2,
                            color: AI_TEXT,
                            '&:hover': { bgcolor: alpha(AI_COLOR, 0.1) },
                          }}
                        >
                          {isVideoInCollection(results.generationId) ? <FaBookmark size={18} /> : <FaRegBookmark size={18} />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* ===== RIGHT SIDEBAR (4 cols) - Instructions ===== */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Instructions
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                  1. Upload 1-3 product reference images<br />
                  2. Choose aspect ratio, duration, and resolution<br />
                  3. Select a prompt template or write your own<br />
                  4. Click "Generate Video" to start<br />
                  5. Wait for generation to complete (may take several minutes)<br />
                  6. Preview and download your video
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', lineHeight: 1.6 }}>
                  {isOmni
                    ? 'Powered by Google Gemini Omni 1.1 Flash — fast video with audio at 360p up to 4K (3–10s clips). Reference images keep the product looking exactly as shown.'
                    : engine === 'veo-fast'
                    ? 'Powered by Google Veo 3.1 Fast on Google Cloud — the cheaper Veo tier, 720p or 1080p (4–8s clips). Reference photos (8s clips) or a literal first frame keep the product as shown.'
                    : 'Powered by Google Veo 3.1 on Google Cloud — 720p up to 4K (4–8s clips). Reference images ensure the product appears exactly as shown; they require 8-second clips, a literal first frame allows 4–8s.'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* ==================== HISTORY TAB ==================== */}
        {mainTab === 'history' && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              <FaHistory color={AI_COLOR} size={18} />
              <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                Generation History
              </Typography>
              {/* Engine filter — view all videos, or only one model's output */}
              <ToggleButtonGroup
                exclusive
                size="small"
                value={historyEngineFilter}
                onChange={(_, v) => { if (v !== null) { setHistoryEngineFilter(v); setHistoryCompareSel([]); setMergeSel([]); } }}
                sx={{
                  '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12, py: 0.4, px: 1.25, color: 'text.secondary' },
                  '& .Mui-selected': { bgcolor: `${alpha(AI_COLOR, 0.15)} !important`, color: `${AI_TEXT} !important`, fontWeight: 600 },
                }}
              >
                <ToggleButton value="">All</ToggleButton>
                <ToggleButton value="veo">Veo 3.1</ToggleButton>
                <ToggleButton value="omni">Omni Flash</ToggleButton>
              </ToggleButtonGroup>
              {historyCompareMode && historyCompareSel.length === 2 && (
                <Button size="small" variant="contained" startIcon={<MdCompare size={15} />}
                  onClick={() => setHistoryCompareOpen(true)}
                  sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none', fontSize: 12 }}>
                  Compare (2)
                </Button>
              )}
              {mergeMode && mergeSel.length >= 2 && (
                <Button size="small" variant="contained" disabled={mergeApplying}
                  startIcon={mergeApplying ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <CallMerge sx={{ fontSize: 16 }} />}
                  onClick={handleMergeVideos}
                  sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none', fontSize: 12 }}>
                  {mergeApplying ? 'Merging…' : `Merge (${mergeSel.length})`}
                </Button>
              )}
              <Button size="small" variant={mergeMode ? 'contained' : 'outlined'} startIcon={<CallMerge sx={{ fontSize: 16 }} />}
                onClick={() => { setMergeMode(m => !m); setMergeSel([]); setHistoryCompareMode(false); setHistoryCompareSel([]); }}
                sx={mergeMode
                  ? { bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none', fontSize: 12 }
                  : { borderColor: AI_COLOR, color: AI_TEXT, textTransform: 'none', fontSize: 12, '&:hover': { borderColor: AI_COLOR, bgcolor: alpha(AI_COLOR, 0.06) } }}>
                {mergeMode ? 'Cancel merge' : 'Merge videos'}
              </Button>
              <Button size="small" variant={historyCompareMode ? 'contained' : 'outlined'} startIcon={<MdCompare size={15} />}
                onClick={() => { setHistoryCompareMode(m => !m); setHistoryCompareSel([]); setMergeMode(false); setMergeSel([]); }}
                sx={historyCompareMode
                  ? { bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none', fontSize: 12 }
                  : { borderColor: AI_COLOR, color: AI_TEXT, textTransform: 'none', fontSize: 12, '&:hover': { borderColor: AI_COLOR, bgcolor: alpha(AI_COLOR, 0.06) } }}>
                {historyCompareMode ? 'Cancel compare' : 'Compare videos'}
              </Button>
              <Chip label={historyTotal} size="small" sx={{ bgcolor: alpha(AI_COLOR, 0.1), color: AI_COLOR, fontWeight: 600, fontSize: 11 }} />
            </Box>
            {historyCompareMode && (
              <Typography variant="caption" sx={{ display: 'block', mt: -2, mb: 2, color: theme.palette.text.secondary }}>
                Select 2 videos to watch them side by side ({historyCompareSel.length}/2 selected)
              </Typography>
            )}
            {mergeMode && (
              <Typography variant="caption" sx={{ display: 'block', mt: -2, mb: 2, color: theme.palette.text.secondary }}>
                Tap videos in the order you want them joined, then Merge ({mergeSel.length} selected). They'll be scaled to the first clip's aspect ratio.
              </Typography>
            )}

            {historyItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <FaVideo size={48} style={{ opacity: 0.2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>No videos generated yet</Typography>
                <Typography variant="body2" color="text.secondary">Generated videos will appear here.</Typography>
                <Button variant="contained" onClick={() => setMainTab('generator')} sx={{ mt: 2, bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}>
                  Go to Generator
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
                  {historyItems.map((item, idx) => {
                    const itemKey = (item.generationId || item.id) || idx;
                    const selectMode = historyCompareMode || mergeMode;
                    const mergeOrder = mergeSel.findIndex(s => (s.generationId || s.id) === (item.generationId || item.id));
                    const isSelected = historyCompareSel.some(s => ((s.generationId || s.id)) === (item.generationId || item.id)) || mergeOrder >= 0;
                    return (
                      <Card
                        key={itemKey}
                        sx={{
                          border: isSelected ? `2px solid ${AI_COLOR}` : `1px solid ${theme.palette.divider}`,
                          transition: 'all 0.2s ease',
                          '&:hover': { borderColor: AI_COLOR },
                        }}
                      >
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                          {/* Video thumbnail — first frame via preload=metadata; click plays (or selects in compare mode) */}
                          <Box
                            onClick={() => {
                              if (historyCompareMode) {
                                setHistoryCompareSel(prev => {
                                  const id = item.generationId || item.id;
                                  if (prev.some(s => (s.generationId || s.id) === id)) {
                                    return prev.filter(s => (s.generationId || s.id) !== id);
                                  }
                                  if (prev.length >= 2) return prev; // cap at 2
                                  return [...prev, item];
                                });
                              } else if (mergeMode) {
                                setMergeSel(prev => {
                                  const id = item.generationId || item.id;
                                  if (prev.some(s => (s.generationId || s.id) === id)) {
                                    return prev.filter(s => (s.generationId || s.id) !== id);
                                  }
                                  if (prev.length >= 8) return prev; // cap at 8
                                  return [...prev, item];
                                });
                              } else {
                                setPlayingHistoryIndex(idx);
                              }
                            }}
                            sx={{
                              height: 170,
                              bgcolor: '#000',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              position: 'relative',
                              overflow: 'hidden',
                              '&:hover .play-ov': { opacity: 1 },
                            }}
                          >
                            {item.s3Url ? (
                              <video src={item.s3Url} muted preload="metadata" playsInline
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            ) : (
                              <FaVideo size={32} style={{ opacity: 0.3, color: '#fff' }} />
                            )}
                            {/* Hover play overlay */}
                            <Box className="play-ov" sx={{
                              position: 'absolute', inset: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: 'rgba(0,0,0,0.35)', opacity: selectMode ? 1 : 0, transition: 'opacity 0.2s',
                            }}>
                              {!selectMode && (
                                <Box sx={{
                                  width: 52, height: 52, borderRadius: '50%',
                                  bgcolor: 'rgba(255,255,255,0.9)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <FaPlay size={18} color={AI_COLOR} style={{ marginLeft: 3 }} />
                                </Box>
                              )}
                            </Box>
                            {/* Merge selection order badge */}
                            {mergeMode && mergeOrder >= 0 && (
                              <Box sx={{ position: 'absolute', top: 6, left: 6, zIndex: 2,
                                width: 24, height: 24, borderRadius: '50%', bgcolor: AI_COLOR, color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
                                {mergeOrder + 1}
                              </Box>
                            )}
                            {/* Compare selection checkbox */}
                            {historyCompareMode && (
                              <Checkbox checked={isSelected} size="small"
                                sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2, p: 0.5, color: '#fff', '&.Mui-checked': { color: AI_COLOR }, bgcolor: 'rgba(0,0,0,0.45)', borderRadius: '4px' }} />
                            )}
                            {item.aspectRatio && !historyCompareMode && (
                              <Chip label={item.aspectRatio} size="small" sx={{
                                position: 'absolute', top: 8, left: 8,
                                height: 20, fontSize: '0.65rem',
                                bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                              }} />
                            )}
                            {item.duration && (
                              <Chip label={typeof item.duration === 'number' ? `${item.duration}s` : item.duration} size="small" sx={{
                                position: 'absolute', top: 8, right: 8,
                                height: 20, fontSize: '0.65rem',
                                bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                              }} />
                            )}
                            {/* Product-fidelity QC badge */}
                            {item.fidelityScore != null && !selectMode && (
                              <Tooltip title={`Product fidelity ${item.fidelityScore}/10 — click for details`}>
                                <Chip
                                  label={`QC ${item.fidelityScore}/10`}
                                  size="small"
                                  clickable
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFidelityDetail({ rubric: parseFidelity(item), score: item.fidelityScore, fileName: item.videoFileName });
                                  }}
                                  sx={{
                                    position: 'absolute', bottom: 8, left: 8,
                                    height: 20, fontSize: '0.65rem', fontWeight: 700,
                                    bgcolor: 'rgba(0,0,0,0.65)', color: fidelityColor(item.fidelityScore),
                                    border: `1px solid ${fidelityColor(item.fidelityScore)}`,
                                  }} />
                              </Tooltip>
                            )}
                          </Box>

                          {/* Card info */}
                          <Box sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{
                              fontWeight: 500, mb: 0.5,
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              fontSize: '0.8rem', lineHeight: 1.4,
                            }}>
                              {item.prompt || item.videoFileName || `Video ${(item.generationId || item.id)?.slice(0, 8) || ''}`}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {item.createdAt ? formatDateTime(item.createdAt) : 'Unknown date'}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {item.inputImages && item.inputImages.length > 0 && (
                                  <Tooltip title="Compare with reference image(s)">
                                    <IconButton size="small" onClick={() => setCompareDialogItem(item)} sx={{ color: AI_TEXT }}>
                                      <MdCompare size={15} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title={isVideoInCollection(item.generationId || item.id) ? 'Saved to Collection' : 'Save to Collection'}>
                                  <IconButton size="small" onClick={(e) => handleOpenSaveMenu(e, {
                                    generationId: (item.generationId || item.id),
                                    prompt: item.prompt || '',
                                    s3Url: item.s3Url || '',
                                    videoFileName: item.videoFileName || '',
                                    duration: item.duration || '',
                                    aspectRatio: item.aspectRatio || '',
                                  })} sx={{ color: isVideoInCollection(item.generationId || item.id) ? AI_TEXT : undefined }}>
                                    {isVideoInCollection(item.generationId || item.id) ? <FaBookmark size={12} /> : <FaRegBookmark size={12} />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit — trim, speed, or loop this video">
                                  <IconButton size="small" onClick={() => openEditor(item)} sx={{ color: AI_TEXT }}>
                                    <EditIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleHistoryDownload((item.generationId || item.id))}
                                    sx={{ color: AI_TEXT }}
                                  >
                                    <FaDownload size={14} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>

                {/* Pagination */}
                {totalHistoryPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalHistoryPages}
                      page={historyPage}
                      onChange={(_, page) => fetchHistory(page)}
                      size="small"
                      sx={{
                        '& .Mui-selected': {
                          bgcolor: `${alpha(AI_COLOR, 0.15)} !important`,
                          color: AI_COLOR,
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Paper>
        )}

        {/* ==================== COLLECTIONS TAB ==================== */}
        {mainTab === 'collections' && (
          <Box sx={{ display: 'flex', gap: 2, minHeight: 500 }}>
            {/* Left Sidebar - Collection List */}
            <Paper sx={{ width: 250, flexShrink: 0, p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
                <MdFolderSpecial color={AI_COLOR} size={20} /> Collections
              </Typography>

              {/* Create Collection */}
              {showCreateCollection ? (
                <Box sx={{ mb: 2 }}>
                  <TextField size="small" fullWidth placeholder="Collection name..." value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') createCollection(newCollectionName); if (e.key === 'Escape') { setShowCreateCollection(false); setNewCollectionName(''); } }}
                    autoFocus sx={{ mb: 1 }} />
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" variant="contained" onClick={() => createCollection(newCollectionName)}
                      disabled={!newCollectionName.trim()}
                      sx={{ flex: 1, bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, fontSize: '0.75rem', textTransform: 'none' }}>
                      Save
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => { setShowCreateCollection(false); setNewCollectionName(''); }}
                      sx={{ fontSize: '0.75rem', textTransform: 'none', borderColor: AI_COLOR, color: theme.palette.text.primary }}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Button size="small" startIcon={<FaPlus size={10} />} variant="contained" fullWidth
                  onClick={() => setShowCreateCollection(true)}
                  sx={{ mb: 2, bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none', fontSize: '0.8rem' }}>
                  Create Collection
                </Button>
              )}

              {/* Collection list */}
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {collections.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, px: 1 }}>
                    <FaFolder size={32} style={{ opacity: 0.15 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      No collections yet
                    </Typography>
                  </Box>
                ) : (
                  collections.map(col => (
                    <Card key={col.id}
                      onClick={() => {
                        setSelectedCollection(col.id);
                        setCollectionCompareMode(false);
                        setCollectionCompareSelected(new Set());
                      }}
                      sx={{
                        mb: 1, cursor: 'pointer',
                        border: selectedCollection === col.id ? `2px solid ${AI_COLOR}` : `1px solid ${theme.palette.divider}`,
                        bgcolor: selectedCollection === col.id ? (isDark ? `${AI_COLOR}15` : `${AI_COLOR}08`) : 'transparent',
                        transition: 'all 0.15s ease', '&:hover': { borderColor: AI_COLOR },
                      }}>
                      <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
                        {editingCollectionId === col.id ? (
                          <Box onClick={(e) => e.stopPropagation()}>
                            <TextField size="small" fullWidth value={editingCollectionName}
                              onChange={(e) => setEditingCollectionName(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') renameCollection(col.id, editingCollectionName); if (e.key === 'Escape') { setEditingCollectionId(null); setEditingCollectionName(''); } }}
                              autoFocus sx={{ mb: 0.5 }} />
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button size="small" onClick={() => renameCollection(col.id, editingCollectionName)}
                                sx={{ fontSize: '0.65rem', textTransform: 'none', color: AI_COLOR }}>Save</Button>
                              <Button size="small" onClick={() => { setEditingCollectionId(null); setEditingCollectionName(''); }}
                                sx={{ fontSize: '0.65rem', textTransform: 'none' }}>Cancel</Button>
                            </Box>
                          </Box>
                        ) : (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {selectedCollection === col.id ? <FaFolderOpen size={14} color={AI_COLOR} /> : <FaFolder size={14} style={{ opacity: 0.5 }} />}
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {col.name}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {col.videos.length} video{col.videos.length !== 1 ? 's' : ''}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                                <Tooltip title="Rename">
                                  <IconButton size="small" onClick={() => { setEditingCollectionId(col.id); setEditingCollectionName(col.name); }}
                                    sx={{ width: 20, height: 20 }}>
                                    <FaEdit size={9} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={() => confirmDeleteCollection(col.id)}
                                    sx={{ width: 20, height: 20, color: 'error.main' }}>
                                    <FaTrash size={9} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', mt: 0.25 }}>
                              {new Date(col.createdAt).toLocaleDateString()}
                            </Typography>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            </Paper>

            {/* Right Area - Selected Collection Videos */}
            <Paper sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
              {!currentCollection ? (
                <Box sx={{ textAlign: 'center', py: 8, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <MdFolderSpecial size={64} style={{ opacity: 0.15, color: AI_COLOR }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>No collection selected</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 400 }}>
                    Save your best AI videos here for easy access. Create a collection from the sidebar, then use the bookmark icon on generated videos to save them.
                  </Typography>
                  <Button variant="contained" startIcon={<FaPlus size={12} />}
                    onClick={() => setShowCreateCollection(true)}
                    sx={{ mt: 3, bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none' }}>
                    Create Your First Collection
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Collection header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaFolderOpen color={AI_COLOR} /> {currentCollection.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {currentCollection.videos.length} video{currentCollection.videos.length !== 1 ? 's' : ''} — Created {new Date(currentCollection.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button size="small" variant="outlined"
                        startIcon={<MdAddCircleOutline size={14} />}
                        onClick={openAddVideosDialog}
                        sx={{ textTransform: 'none', fontSize: '0.8rem', borderColor: AI_COLOR, color: theme.palette.text.primary }}>
                        Add Videos
                      </Button>
                      {currentCollection.videos.length >= 2 && (
                        <Button size="small"
                          variant={collectionCompareMode ? 'contained' : 'outlined'}
                          startIcon={<MdCompare size={14} />}
                          onClick={() => {
                            setCollectionCompareMode(!collectionCompareMode);
                            setCollectionCompareSelected(new Set());
                          }}
                          sx={{
                            textTransform: 'none', fontSize: '0.8rem',
                            ...(collectionCompareMode
                              ? { bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }
                              : { borderColor: AI_COLOR, color: theme.palette.text.primary }),
                          }}>
                          {collectionCompareMode ? 'Exit Compare' : 'Compare'}
                        </Button>
                      )}
                      {collectionCompareMode && collectionCompareSelected.size >= 2 && (
                        <Button size="small" variant="contained"
                          onClick={() => setComparisonDialogOpen(true)}
                          sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none', fontSize: '0.8rem' }}>
                          Compare Selected ({collectionCompareSelected.size})
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {collectionCompareMode && (
                    <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                      Select 2-4 videos to compare side by side. Click video cards to select them.
                    </Alert>
                  )}

                  {/* Collection videos grid */}
                  {currentCollection.videos.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <FaVideo size={48} style={{ opacity: 0.15 }} />
                      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>This collection is empty</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Use the bookmark icon on generated or history videos to add them here.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
                      {currentCollection.videos.map((colVid, idx) => {
                        const isSelected = collectionCompareSelected.has(idx);
                        const canSelect = collectionCompareMode && (isSelected || collectionCompareSelected.size < 4);
                        return (
                          <Card key={colVid.id || idx} sx={{
                            border: isSelected ? `3px solid ${AI_COLOR}` : `1px solid ${theme.palette.divider}`,
                            position: 'relative',
                            transition: 'all 0.15s ease',
                            opacity: collectionCompareMode && !canSelect && !isSelected ? 0.5 : 1,
                            cursor: collectionCompareMode ? (canSelect ? 'pointer' : 'not-allowed') : 'default',
                            '&:hover': collectionCompareMode ? {} : { borderColor: AI_COLOR },
                          }}
                            onClick={() => {
                              if (!collectionCompareMode) return;
                              if (isSelected) {
                                setCollectionCompareSelected(prev => { const next = new Set(prev); next.delete(idx); return next; });
                              } else if (canSelect) {
                                setCollectionCompareSelected(prev => new Set([...prev, idx]));
                              }
                            }}>
                            {/* Compare mode checkbox overlay */}
                            {collectionCompareMode && (
                              <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2 }}>
                                <Checkbox size="small" checked={isSelected}
                                  disabled={!canSelect && !isSelected}
                                  sx={{ p: 0, color: 'white', '&.Mui-checked': { color: AI_COLOR }, bgcolor: 'rgba(0,0,0,0.4)', borderRadius: '4px', width: 24, height: 24 }}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => {
                                    if (isSelected) {
                                      setCollectionCompareSelected(prev => { const next = new Set(prev); next.delete(idx); return next; });
                                    } else if (canSelect) {
                                      setCollectionCompareSelected(prev => new Set([...prev, idx]));
                                    }
                                  }} />
                              </Box>
                            )}
                            {isSelected && (
                              <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, bgcolor: AI_COLOR, color: 'white', width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                                {[...collectionCompareSelected].sort((a, b) => a - b).indexOf(idx) + 1}
                              </Box>
                            )}

                            {/* Play icon area */}
                            <Box sx={{
                              height: 120,
                              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                            }}>
                              <Box sx={{
                                width: 48, height: 48, borderRadius: '50%',
                                bgcolor: alpha(AI_COLOR, 0.15),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <FaPlay size={18} color={AI_COLOR} style={{ marginLeft: 2 }} />
                              </Box>
                              {colVid.aspectRatio && (
                                <Chip label={colVid.aspectRatio} size="small" sx={{
                                  position: 'absolute', bottom: 6, left: 6,
                                  height: 18, fontSize: '0.6rem',
                                  bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                                }} />
                              )}
                              {colVid.duration && (
                                <Chip label={colVid.duration} size="small" sx={{
                                  position: 'absolute', bottom: 6, right: 6,
                                  height: 18, fontSize: '0.6rem',
                                  bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                                }} />
                              )}
                            </Box>

                            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                              <Typography variant="caption" sx={{
                                fontWeight: 500, fontSize: '0.7rem',
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                lineHeight: 1.4, mb: 0.5,
                              }}>
                                {colVid.prompt || colVid.videoFileName || 'Untitled'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                  {colVid.addedAt ? new Date(colVid.addedAt).toLocaleDateString() : ''}
                                </Typography>
                                {!collectionCompareMode && (
                                  <Tooltip title="Remove from collection">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeFromCollection(currentCollection.id, idx); }}
                                      sx={{ width: 20, height: 20, color: 'error.main' }}>
                                      <FaTimes size={9} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Box>
        )}

        {/* ==================== SAVE TO COLLECTION MENU ==================== */}
        <Menu anchorEl={saveMenuAnchor} open={Boolean(saveMenuAnchor)}
          onClose={() => { handleCloseSaveMenu(); setShowCreateInMenu(false); setNewColNameInMenu(''); }}
          PaperProps={{ sx: { minWidth: 250, maxHeight: 400 } }}>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: AI_TEXT, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 1 }}>
              Save to Collection
            </Typography>
          </Box>
          <Divider />
          {collections.length === 0 && !showCreateInMenu ? (
            <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">No collections yet — create one below</Typography>
            </Box>
          ) : (
            collections.map(col => {
              const inCol = (col.videos || []).some(v => v.generationId === saveMenuVideoData?.generationId);
              return (
                <MenuItem key={col.id} onClick={() => toggleVideoInCollection(col)} sx={{ fontSize: '0.85rem' }}>
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <Checkbox checked={inCol} size="small" disableRipple
                      sx={{ p: 0, '&.Mui-checked': { color: AI_COLOR } }} />
                  </ListItemIcon>
                  <ListItemText primary={col.name} secondary={`${(col.videos || []).length} videos`}
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: inCol ? 600 : 500 }}
                    secondaryTypographyProps={{ fontSize: '0.65rem' }} />
                </MenuItem>
              );
            })
          )}
          <Divider />
          {showCreateInMenu ? (
            <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 0.5, alignItems: 'center' }}
              onKeyDown={(e) => e.stopPropagation() /* keep Menu type-ahead from stealing keystrokes */}>
              <TextField size="small" autoFocus fullWidth placeholder="Collection name..."
                value={newColNameInMenu} onChange={(e) => setNewColNameInMenu(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createCollectionAndAddCurrent(); if (e.key === 'Escape') { setShowCreateInMenu(false); setNewColNameInMenu(''); } }}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }} />
              <Button size="small" variant="contained" disabled={!newColNameInMenu.trim()}
                onClick={createCollectionAndAddCurrent}
                sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none', fontSize: 12, flexShrink: 0 }}>
                Add
              </Button>
            </Box>
          ) : (
            <MenuItem onClick={() => setShowCreateInMenu(true)} sx={{ fontSize: '0.85rem', color: AI_TEXT }}>
              <ListItemIcon sx={{ minWidth: 34 }}>
                <MdAddCircleOutline size={16} color={AI_TEXT} />
              </ListItemIcon>
              <ListItemText primary="Create New Collection" primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
            </MenuItem>
          )}
        </Menu>

        {/* ==================== COMPARISON DIALOG ==================== */}
        <Dialog open={comparisonDialogOpen} onClose={() => setComparisonDialogOpen(false)}
          maxWidth="xl" fullWidth
          fullScreen={window.innerWidth < 600}
          PaperProps={{ sx: { minHeight: '70vh' } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `2px solid ${AI_COLOR}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MdCompare color={AI_COLOR} size={22} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Video Comparison</Typography>
              <Chip label={`${comparisonVideos.length} videos`} size="small" sx={{ bgcolor: `${AI_COLOR}20`, color: AI_COLOR }} />
            </Box>
            <IconButton onClick={() => setComparisonDialogOpen(false)}>
              <FaTimes size={16} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: comparisonVideos.length === 2 ? '1fr 1fr' :
                comparisonVideos.length === 3 ? '1fr 1fr 1fr' :
                  '1fr 1fr',
              gap: 3,
              mt: 1,
            }}>
              {comparisonVideos.map((colVid, idx) => (
                <Box key={colVid.id || idx} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `2px solid ${AI_COLOR}`, bgcolor: '#000' }}>
                    {colVid.s3Url ? (
                      <video
                        controls
                        style={{ width: '100%', display: 'block', maxHeight: 350 }}
                        src={colVid.s3Url}
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="text.secondary">No video URL available</Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: 'center' }}>
                    {colVid.videoFileName || `Video ${idx + 1}`}
                  </Typography>
                  {colVid.prompt && (
                    <Box sx={{ p: 1, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 1, maxHeight: 100, overflow: 'auto' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {colVid.prompt}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {colVid.aspectRatio && <Chip label={colVid.aspectRatio} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                    {colVid.duration && <Chip label={colVid.duration} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                  </Box>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setComparisonDialogOpen(false)} sx={{ color: theme.palette.text.secondary }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ==================== DELETE COLLECTION CONFIRM DIALOG ==================== */}
        <Dialog open={deleteConfirmOpen} onClose={() => { setDeleteConfirmOpen(false); setDeleteConfirmId(null); }}>
          <DialogTitle>Delete Collection</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Are you sure you want to delete this collection? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirmId(null); }}
              sx={{ color: theme.palette.text.secondary }}>
              Cancel
            </Button>
            <Button onClick={() => deleteCollection(deleteConfirmId)}
              variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== ADD VIDEOS TO COLLECTION DIALOG ==================== */}
        <Dialog open={addVideosDialogOpen} onClose={() => { setAddVideosDialogOpen(false); setAddVideosPreviewId(null); }} maxWidth="lg" fullWidth
          PaperProps={{ sx: { bgcolor: isDark ? '#1e1e2e' : '#fff', minHeight: 560 } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <MdAddCircleOutline size={20} color={AI_COLOR} />
            <Box sx={{ flex: 1 }}>
              Add Videos to "{currentCollection?.name}"
              <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary, fontWeight: 400 }}>
                Click a video to preview it · hover for the prompt · use + Add to save it to the collection
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => { setAddVideosDialogOpen(false); setAddVideosPreviewId(null); }}><FaTimes size={16} /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2 }}>
            {addVideosLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <Typography color="text.secondary">Loading videos...</Typography>
              </Box>
            ) : addVideosItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary">No video history found. Generate some videos first.</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                  {addVideosItems.map((item, idx) => {
                    const vidId = (item.generationId || item.id);
                    const alreadyAdded = currentCollection?.videos.some(v => v.generationId === vidId);
                    const isPreviewing = addVideosPreviewId === vidId;
                    return (
                      <Tooltip key={vidId || idx} title={item.prompt || ''} arrow placement="top" disableInteractive
                        enterDelay={500} slotProps={{ tooltip: { sx: { maxWidth: 380, fontSize: 11.5, lineHeight: 1.5 } } }}>
                        <Card sx={{
                          border: alreadyAdded ? `2px solid ${AI_COLOR}` : `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          transition: 'border-color 0.2s',
                          '&:hover': { borderColor: AI_COLOR },
                        }}>
                          {/* Thumbnail / inline preview */}
                          <Box
                            onClick={() => { if (!isPreviewing) setAddVideosPreviewId(vidId); }}
                            sx={{ position: 'relative', height: 160, bgcolor: '#000', cursor: isPreviewing ? 'default' : 'pointer', '&:hover .pv-ov': { opacity: 1 } }}>
                            {isPreviewing ? (
                              <>
                                <video src={item.s3Url} controls autoPlay playsInline
                                  ref={(el) => {
                                    if (!el) return;
                                    const p = el.play();
                                    if (p && p.catch) p.catch(() => { el.muted = true; el.play().catch(() => {}); });
                                  }}
                                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }} />
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAddVideosPreviewId(null); }}
                                  sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', p: 0.4, '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' } }}>
                                  <FaTimes size={12} />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                {item.s3Url ? (
                                  <video src={item.s3Url} muted preload="metadata" playsInline
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                ) : (
                                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Videocam sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 32 }} />
                                  </Box>
                                )}
                                <Box className="pv-ov" sx={{
                                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  bgcolor: 'rgba(0,0,0,0.35)', opacity: 0, transition: 'opacity 0.2s',
                                }}>
                                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaPlay size={15} color={AI_COLOR} style={{ marginLeft: 2 }} />
                                  </Box>
                                </Box>
                                {item.duration && (
                                  <Chip label={typeof item.duration === 'number' ? `${item.duration}s` : item.duration} size="small"
                                    sx={{ position: 'absolute', bottom: 6, right: 6, height: 18, fontSize: '0.6rem', bgcolor: 'rgba(0,0,0,0.65)', color: '#fff' }} />
                                )}
                                {alreadyAdded && (
                                  <Chip label="✓ In collection" size="small"
                                    sx={{ position: 'absolute', top: 6, left: 6, height: 18, fontSize: '0.6rem', bgcolor: AI_COLOR, color: '#fff' }} />
                                )}
                              </>
                            )}
                          </Box>
                          {/* Info + action */}
                          <Box sx={{ px: 1.2, py: 0.9, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                {item.videoFileName || item.prompt || `Video ${vidId || ''}`}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.65rem' }}>
                                {item.createdAt ? formatDateTime(item.createdAt) : ''}
                                {item.aspectRatio ? ` · ${item.aspectRatio}` : ''}
                              </Typography>
                            </Box>
                            {alreadyAdded ? (
                              <Button size="small" variant="outlined"
                                onClick={() => {
                                  const idx2 = currentCollection?.videos.findIndex(v => v.generationId === vidId);
                                  if (idx2 >= 0) removeFromCollection(currentCollection.id, idx2);
                                }}
                                sx={{ textTransform: 'none', fontSize: 11, py: 0.2, px: 1, minWidth: 0, flexShrink: 0,
                                  borderColor: AI_COLOR, color: AI_TEXT,
                                  '&:hover': { borderColor: '#a33', color: '#a33', bgcolor: alpha('#a33', 0.06) } }}>
                                ✓ Added
                              </Button>
                            ) : (
                              <Button size="small" variant="contained" startIcon={<FaPlus size={9} />}
                                onClick={() => {
                                  if (currentCollection) {
                                    addToCollection(currentCollection.id, {
                                      generationId: vidId,
                                      prompt: item.prompt || '',
                                      s3Url: item.s3Url || '',
                                      videoFileName: item.videoFileName || '',
                                      duration: item.duration || '',
                                      aspectRatio: item.aspectRatio || '',
                                    });
                                  }
                                }}
                                sx={{ textTransform: 'none', fontSize: 11, py: 0.3, px: 1.2, minWidth: 0, flexShrink: 0,
                                  bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}>
                                Add
                              </Button>
                            )}
                          </Box>
                        </Card>
                      </Tooltip>
                    );
                  })}
                </Box>

                {addVideosTotalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={addVideosTotalPages} page={addVideosPage}
                      onChange={(_, p) => fetchAddVideosPage(p)} size="small"
                      sx={{ '& .Mui-selected': { bgcolor: `${alpha(AI_COLOR, 0.15)} !important`, color: AI_COLOR } }} />
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setAddVideosDialogOpen(false); setAddVideosPreviewId(null); }} sx={{ color: theme.palette.text.secondary }}>Done</Button>
          </DialogActions>
        </Dialog>

        {/* ==================== ADD/EDIT PROMPT DIALOG ==================== */}
        <Dialog open={showPromptDialog} onClose={() => { setShowPromptDialog(false); setEditingPrompt(null); }} maxWidth="sm" fullWidth
          PaperProps={{ sx: { bgcolor: isDark ? '#1e1e2e' : '#fff' } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <FaBookmark size={16} color={AI_COLOR} />
            {editingPrompt ? 'Edit Prompt' : 'Add New Prompt'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {promptSaveError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPromptSaveError('')}>
                {promptSaveError}
              </Alert>
            )}
            <TextField
              fullWidth
              size="small"
              label="Prompt Name"
              value={editPromptName}
              onChange={(e) => setEditPromptName(e.target.value)}
              sx={{
                mt: 1, mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': { borderColor: AI_COLOR },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: AI_COLOR },
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Prompt Text"
              value={editPromptText}
              onChange={(e) => setEditPromptText(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  fontSize: 13,
                  '&.Mui-focused fieldset': { borderColor: AI_COLOR },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: AI_COLOR },
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={editPromptIsPublic}
                onChange={(e) => setEditPromptIsPublic(e.target.checked)}
                size="small"
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4caf50' } }}
              />
              {editPromptIsPublic ? <FaGlobe size={14} color="#4caf50" /> : <FaLock size={14} color={theme.palette.text.secondary} />}
              <Typography variant="body2" sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                {editPromptIsPublic ? 'Shared (visible to all users)' : 'Private (only visible to you)'}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setShowPromptDialog(false); setEditingPrompt(null); }}
              sx={{ color: theme.palette.text.secondary, textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePrompt}
              variant="contained"
              disabled={!editPromptName.trim() || !editPromptText.trim()}
              sx={{
                textTransform: 'none',
                bgcolor: AI_COLOR,
                '&:hover': { bgcolor: TOOL_COLORS.AI.hover },
              }}
            >
              {editingPrompt ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== HISTORY VIDEO PLAYER (prev/next scrolling, admin-style) ==================== */}
        <Dialog open={playingHistoryIndex >= 0} onClose={() => { setPlayingHistoryIndex(-1); setPlayerPromptExpanded(false); setPlayerPromptCopied(false); }} maxWidth="md" fullWidth
          PaperProps={{ sx: { bgcolor: '#000', overflow: 'hidden' } }}>
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton onClick={() => { setPlayingHistoryIndex(-1); setPlayerPromptExpanded(false); setPlayerPromptCopied(false); }}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
              <FaTimes size={18} />
            </IconButton>
            {playingHistoryIndex > 0 && (
              <IconButton onClick={() => { setPlayingHistoryIndex(prev => prev - 1); setPlayerPromptExpanded(false); setPlayerPromptCopied(false); }}
                sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                <FaChevronLeft size={24} />
              </IconButton>
            )}
            {playingHistoryIndex >= 0 && playingHistoryIndex < historyItems.length - 1 && (
              <IconButton onClick={() => { setPlayingHistoryIndex(prev => prev + 1); setPlayerPromptExpanded(false); setPlayerPromptCopied(false); }}
                sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                <FaChevronRight size={24} />
              </IconButton>
            )}
            {playingHistoryIndex >= 0 && historyItems[playingHistoryIndex] && (
              <video key={(historyItems[playingHistoryIndex].generationId || historyItems[playingHistoryIndex].id)}
                src={historyItems[playingHistoryIndex].s3Url} controls autoPlay playsInline
                ref={(el) => {
                  playerVideoRef.current = el;
                  // Browsers block un-muted autoplay on low-engagement sites (works on
                  // localhost, blocked on prod). Try with sound; fall back to muted.
                  if (!el) return;
                  const p = el.play();
                  if (p && p.catch) p.catch(() => { el.muted = true; el.play().catch(() => {}); });
                }}
                style={{ width: '100%', maxHeight: '80vh', display: 'block' }} />
            )}
            {playingHistoryIndex >= 0 && historyItems[playingHistoryIndex] && (() => {
              const cur = historyItems[playingHistoryIndex];
              const curId = cur.generationId || cur.id;
              const inCollection = isVideoInCollection(curId);
              const actionSx = {
                textTransform: 'none', fontSize: 12, fontWeight: 500, px: 1.25, py: 0.4, minWidth: 0,
                color: isDark ? '#bbb' : 'text.secondary', borderRadius: 1.5,
                '&:hover': { color: AI_COLOR, bgcolor: alpha(AI_COLOR, isDark ? 0.18 : 0.08) },
              };
              return (
                <Box sx={{ px: 2, pt: 1.25, pb: 1, bgcolor: isDark ? 'rgba(0,0,0,0.85)' : 'background.paper', borderTop: isDark ? 'none' : `1px solid ${theme.palette.divider}` }}>
                  {/* Row 1 — file info (left) + metadata chips (right) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" noWrap sx={{ color: isDark ? '#999' : 'text.secondary', minWidth: 0, flexShrink: 1 }}>
                      {cur.videoFileName || ''} — {playingHistoryIndex + 1} of {historyItems.length}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                      {cur.fidelityScore != null && (
                        <Tooltip title="Product fidelity — click for the full breakdown">
                          <Chip
                            label={`QC ${cur.fidelityScore}/10`}
                            size="small"
                            clickable
                            onClick={() => setFidelityDetail({ rubric: parseFidelity(cur), score: cur.fidelityScore, fileName: cur.videoFileName })}
                            sx={{ height: 20, fontSize: 10, fontWeight: 700,
                              bgcolor: alpha(fidelityColor(cur.fidelityScore), isDark ? 0.22 : 0.12),
                              color: fidelityColor(cur.fidelityScore),
                              border: `1px solid ${alpha(fidelityColor(cur.fidelityScore), 0.5)}` }} />
                        </Tooltip>
                      )}
                      {[
                        cur.engine && engineLabel(cur.engine),
                        cur.resolution,
                        cur.aspectRatio,
                        cur.duration && `${cur.duration}s`,
                      ].filter(Boolean).map((label) => (
                        <Chip key={label} label={label} size="small"
                          sx={{ height: 20, fontSize: 10, fontWeight: 600, letterSpacing: 0.2,
                            bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.07)',
                            color: isDark ? '#ddd' : 'text.primary' }} />
                      ))}
                    </Box>
                  </Box>

                  {/* Row 2 — prompt (click or chevron to expand) + copy */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.75 }}>
                    <Typography
                      variant="body2"
                      onClick={() => {
                        // Don't collapse/expand when the user is selecting text to copy a part of it.
                        if (window.getSelection && window.getSelection().toString()) return;
                        if (cur.prompt) setPlayerPromptExpanded(v => !v);
                      }}
                      sx={{
                        color: isDark ? '#fff' : 'text.primary', lineHeight: 1.5, flex: 1, minWidth: 0,
                        userSelect: 'text',
                        cursor: cur.prompt ? 'pointer' : 'default',
                        ...(playerPromptExpanded
                          ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '32vh', overflowY: 'auto', pr: 0.5 }
                          : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
                      }}
                    >
                      {cur.prompt || 'No prompt'}
                    </Typography>
                    {cur.prompt && (
                      <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        <Tooltip title={playerPromptCopied ? 'Copied!' : 'Copy prompt'}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(cur.prompt)
                                .then(() => {
                                  // In-place feedback: icon flips to a green check for a moment.
                                  if (playerPromptCopiedTimer.current) clearTimeout(playerPromptCopiedTimer.current);
                                  setPlayerPromptCopied(true);
                                  playerPromptCopiedTimer.current = setTimeout(() => setPlayerPromptCopied(false), 1600);
                                })
                                .catch(() => {});
                            }}
                            sx={{ color: playerPromptCopied ? '#66bb6a' : (isDark ? '#aaa' : 'text.secondary'), '&:hover': { color: playerPromptCopied ? '#66bb6a' : (isDark ? '#fff' : 'text.primary') } }}
                          >
                            {playerPromptCopied ? <Check sx={{ fontSize: 15 }} /> : <ContentCopy sx={{ fontSize: 15 }} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={playerPromptExpanded ? 'Show less' : 'Show full prompt'}>
                          <IconButton
                            size="small"
                            onClick={() => setPlayerPromptExpanded(v => !v)}
                            sx={{ color: isDark ? '#aaa' : 'text.secondary', '&:hover': { color: isDark ? '#fff' : 'text.primary' } }}
                          >
                            {playerPromptExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>

                  {/* Row 3 — action bar */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap',
                    mt: 1, pt: 0.75,
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.divider}`,
                  }}>
                    {cur.inputImages?.length > 0 && (
                      <Button size="small" startIcon={<MdCompare size={14} />} sx={actionSx}
                        onClick={() => { playerVideoRef.current?.pause(); setCompareDialogItem(cur); }}>
                        Compare
                      </Button>
                    )}
                    <Button size="small" startIcon={<ContentCut sx={{ fontSize: 14 }} />} sx={actionSx}
                      onClick={() => { playerVideoRef.current?.pause(); openEditor(cur); }}>
                      Trim
                    </Button>
                    {cur.prompt && (
                      <Button size="small" startIcon={<AutoFixHigh sx={{ fontSize: 15 }} />} sx={actionSx}
                        onClick={() => { setFixItIssues([]); setFixItNotes(''); setFixItEditInPlace(true); setFixItOpen(true); }}>
                        Fix it
                      </Button>
                    )}
                    {(!cur.engine || isVeoEngine(cur.engine)) && (() => {
                      // Veo (Google Cloud) extends 720p / 1080p clips of up to 30 s.
                      const res = String(cur.resolution).toLowerCase();
                      const resOk = res === '720p' || res === '1080p';
                      const lenOk = !(Number(cur.duration) > 30);
                      return (
                        <Tooltip title={!resOk
                          ? 'Veo can extend 720p and 1080p clips — this one is ' + (cur.resolution || 'unknown')
                          : !lenOk
                          ? 'Veo can extend clips of up to 30 seconds — this one is ' + cur.duration + 's'
                          : 'Veo continues this clip +7 seconds, conditioned on its final second'}>
                          <span>
                            <Button size="small" startIcon={<Videocam sx={{ fontSize: 15 }} />}
                              sx={actionSx}
                              disabled={!resOk || !lenOk}
                              onClick={() => { playerVideoRef.current?.pause(); setExtendPrompt(''); setExtendOpen(true); }}>
                              Extend +7s
                            </Button>
                          </span>
                        </Tooltip>
                      );
                    })()}
                    <Button size="small"
                      startIcon={inCollection ? <FaBookmark size={11} /> : <FaRegBookmark size={11} />}
                      sx={{ ...actionSx, ...(inCollection && { color: AI_COLOR }) }}
                      onClick={(e) => handleOpenSaveMenu(e, {
                        generationId: curId,
                        prompt: cur.prompt || '',
                        s3Url: cur.s3Url || '',
                        videoFileName: cur.videoFileName || '',
                        duration: cur.duration || '',
                        aspectRatio: cur.aspectRatio || '',
                      })}>
                      {inCollection ? 'Saved' : 'Save'}
                    </Button>
                    <Button size="small" startIcon={<FaDownload size={11} />} sx={actionSx}
                      onClick={() => handleHistoryDownload(curId)}>
                      Download
                    </Button>
                    {cur.inputImages?.length > 0 && (
                      <Button size="small" disabled={fidelityChecking}
                        startIcon={fidelityChecking ? <CircularProgress size={12} /> : <FactCheck sx={{ fontSize: 15 }} />}
                        sx={actionSx}
                        onClick={handleCheckFidelity}>
                        {fidelityChecking ? 'Checking…' : cur.fidelityScore != null ? 'Re-check QC' : 'Check fidelity'}
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* ==================== AI DIRECTOR — 3 PROMPT OPTIONS ==================== */}
        <Dialog open={directorDialogOpen} onClose={() => setDirectorDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
            <AutoAwesome sx={{ color: AI_COLOR, fontSize: 20 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.3 }}>AI Director — pick your shot</Typography>
              <Typography variant="caption" color="text.secondary">
                Tailored for {duration}s · {aspectRatio} · {videoMode}{directorUseIdea ? ' · built on your prompt' : ''}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setDirectorDialogOpen(false)}><FaTimes size={14} /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
              {directorPrompts.map((p, idx) => {
                const saved = directorSavedIdx.includes(idx);
                return (
                  <Paper key={idx} variant="outlined" sx={{ p: 1.75, borderRadius: 2,
                    borderColor: 'divider', '&:hover': { borderColor: AI_COLOR } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: AI_TEXT, flex: 1, minWidth: 0 }}>
                        {idx + 1}. {p.name}
                      </Typography>
                      <Tooltip title={directorCopiedIdx === idx ? 'Copied!' : 'Copy prompt'}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(p.text).then(() => {
                              if (directorCopiedTimer.current) clearTimeout(directorCopiedTimer.current);
                              setDirectorCopiedIdx(idx);
                              directorCopiedTimer.current = setTimeout(() => setDirectorCopiedIdx(-1), 1600);
                            }).catch(() => {});
                          }}
                          sx={{ color: directorCopiedIdx === idx ? '#66bb6a' : 'text.secondary',
                            '&:hover': { color: directorCopiedIdx === idx ? '#66bb6a' : 'text.primary' } }}
                        >
                          {directorCopiedIdx === idx ? <Check sx={{ fontSize: 15 }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.55,
                      maxHeight: 130, overflowY: 'auto', whiteSpace: 'pre-wrap', mb: 1.25,
                      userSelect: 'text', cursor: 'text' }}>
                      {p.text}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={saved || directorSavingIdx === idx}
                        onClick={() => handleSaveDirectorPrompt(idx)}
                        startIcon={directorSavingIdx === idx
                          ? <CircularProgress size={13} />
                          : saved ? <Check sx={{ fontSize: 15 }} /> : <FaRegBookmark size={12} />}
                        sx={{ textTransform: 'none',
                          ...(saved
                            ? { borderColor: '#66bb6a', color: '#66bb6a', '&.Mui-disabled': { borderColor: '#66bb6a', color: '#66bb6a' } }
                            : { borderColor: theme.palette.divider, color: 'text.secondary' }) }}
                      >
                        {saved ? 'Saved to library' : directorSavingIdx === idx ? 'Saving…' : 'Save to library'}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setPrompt(p.text);
                          setActivePromptName(p.name);
                          setDirectorDialogOpen(false);
                        }}
                        sx={{ textTransform: 'none', bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}
                      >
                        Use this prompt
                      </Button>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              size="small"
              disabled={directorLoading}
              onClick={handleAiDirector}
              startIcon={directorLoading ? <CircularProgress size={13} /> : <AutoAwesome sx={{ fontSize: 15 }} />}
              sx={{ color: AI_TEXT, textTransform: 'none', mr: 'auto' }}
            >
              {directorLoading ? 'Directing…' : 'Try 3 more'}
            </Button>
            <Button onClick={() => setDirectorDialogOpen(false)} sx={{ color: theme.palette.text.secondary }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ==================== VIDEO EDITOR — TRIM / SPEED / LOOP ==================== */}
        <Dialog open={!!editorItem} onClose={() => !editApplying && setEditorItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <ContentCut sx={{ color: AI_COLOR, fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', flex: 1 }}>Edit video</Typography>
            <IconButton size="small" disabled={editApplying} onClick={() => setEditorItem(null)}><FaTimes size={14} /></IconButton>
          </DialogTitle>
          <DialogContent>
            {editorItem && (
              <>
                <Box sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: '#000', mb: 2 }}>
                  <video
                    ref={editorVideoRef}
                    src={editorItem.s3Url}
                    controls
                    playsInline
                    onLoadedMetadata={(e) => {
                      const d = e.target.duration;
                      if (d && isFinite(d)) { setEditorVideoDur(d); setEditTrim([0, d]); }
                    }}
                    onTimeUpdate={(e) => {
                      // Stop the "preview trim" playback exactly at the end handle.
                      const stopAt = editorPreviewStopAt.current;
                      if (stopAt != null && e.target.currentTime >= stopAt) {
                        e.target.pause();
                        editorPreviewStopAt.current = null;
                      }
                    }}
                    style={{ width: '100%', maxHeight: 260, display: 'block' }} />
                </Box>

                {/* Trim — dragging a handle scrubs the preview to that exact frame */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}>
                    <ContentCut sx={{ fontSize: 16, color: AI_TEXT }} /> Trim
                  </Typography>
                  <Button size="small" startIcon={<FaPlay size={10} />} onClick={handlePreviewTrim}
                    sx={{ textTransform: 'none', fontSize: 12, color: AI_TEXT }}>
                    Preview trimmed clip
                  </Button>
                </Box>
                <Box sx={{ px: 1 }}>
                  <Slider
                    value={editTrim}
                    onChange={handleTrimChange}
                    min={0} max={Math.max(editorVideoDur, 0.1)} step={0.05}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v.toFixed(2)}s`}
                    disableSwap
                    sx={{ color: AI_COLOR, mt: 1 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">
                      Starts at <b>{editTrim[0].toFixed(2)}s</b>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Keeps <b>{Math.max(0, editTrim[1] - editTrim[0]).toFixed(2)}s</b>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ends at <b>{editTrim[1].toFixed(2)}s</b>
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled' }}>
                    Drag a handle to jump the preview to that exact frame. The result is saved as a new video in History — the original is untouched.
                  </Typography>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button disabled={editApplying} onClick={() => setEditorItem(null)} sx={{ color: theme.palette.text.secondary }}>Cancel</Button>
            <Button variant="contained" onClick={handleApplyEdit}
              disabled={editApplying || (editTrim[1] - editTrim[0]) < 0.2}
              startIcon={editApplying ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <ContentCut sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}>
              {editApplying ? 'Rendering…' : 'Apply & save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== PRODUCT FIDELITY QC DETAIL ==================== */}
        <Dialog open={!!fidelityDetail} onClose={() => setFidelityDetail(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <FactCheck sx={{ color: fidelityDetail ? fidelityColor(fidelityDetail.score) : AI_COLOR, fontSize: 20 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.3 }}>Product fidelity</Typography>
              {fidelityDetail?.fileName && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{fidelityDetail.fileName}</Typography>
              )}
            </Box>
            {fidelityDetail?.rubric?.verdict && (
              <Chip label={fidelityDetail.rubric.verdict.toUpperCase()} size="small"
                sx={{ fontWeight: 700, fontSize: 10, height: 22,
                  bgcolor: alpha(fidelityColor(fidelityDetail.score), 0.15),
                  color: fidelityColor(fidelityDetail.score) }} />
            )}
            <IconButton size="small" onClick={() => setFidelityDetail(null)}><FaTimes size={14} /></IconButton>
          </DialogTitle>
          <DialogContent>
            {fidelityDetail && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: fidelityColor(fidelityDetail.score), lineHeight: 1 }}>
                    {fidelityDetail.score}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">/ 10 overall — how faithfully the product matches its reference images</Typography>
                </Box>
                {fidelityDetail.rubric ? (
                  <>
                    {[
                      ['Pattern & texture', fidelityDetail.rubric.pattern],
                      ['Color match', fidelityDetail.rubric.color],
                      ['Shape & proportions', fidelityDetail.rubric.shape],
                      ['Artifact-free', fidelityDetail.rubric.artifacts],
                    ].filter(([, v]) => v != null).map(([label, v]) => (
                      <Box key={label} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>
                          <Typography variant="caption" sx={{ color: fidelityColor(v), fontWeight: 700 }}>{v}/10</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={v * 10}
                          sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover',
                            '& .MuiLinearProgress-bar': { bgcolor: fidelityColor(v), borderRadius: 3 } }} />
                      </Box>
                    ))}
                    {fidelityDetail.rubric.notes && (
                      <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
                        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{fidelityDetail.rubric.notes}</Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">No detailed rubric stored for this check.</Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setFidelityDetail(null)} sx={{ color: theme.palette.text.secondary }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ==================== EXTEND +7s (VEO SCENE EXTENSION) ==================== */}
        <Dialog open={extendOpen} onClose={() => !extendLoading && setExtendOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <Videocam sx={{ color: AI_COLOR, fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', flex: 1 }}>Extend video +7 seconds</Typography>
            <IconButton size="small" disabled={extendLoading} onClick={() => setExtendOpen(false)}><FaTimes size={14} /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Veo continues this clip, conditioned on its final second. Describe what happens next — the combined, longer video lands in History as a new entry (clips of up to 30s can be extended, so up to ~37s in total).
            </Typography>
            <TextField
              autoFocus
              multiline
              rows={3}
              fullWidth
              label="What happens in the next 7 seconds?"
              placeholder="e.g. the camera keeps pulling back as golden-hour light floods the room, revealing the full seating area around the rug"
              value={extendPrompt}
              onChange={(e) => setExtendPrompt(e.target.value)}
            />
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              {(() => {
                const item = historyItems[playingHistoryIndex];
                const eng = item?.engine === 'veo-fast' ? 'veo-fast' : 'veo';
                const res = String(item?.resolution || '720p').toLowerCase();
                return `~$${estimateVideoCost(eng, res, 7, true).toFixed(2)} per extension with sound, ~$${estimateVideoCost(eng, res, 7, false).toFixed(2)} if the clip is silent (7s · ${engineLabel(eng)} · ${res})`;
              })()} · re-state the product details in your description for best continuity
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button disabled={extendLoading} onClick={() => setExtendOpen(false)} sx={{ color: theme.palette.text.secondary }}>Cancel</Button>
            <Button variant="contained" onClick={handleExtendVideo}
              disabled={extendLoading || !extendPrompt.trim()}
              startIcon={extendLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Videocam sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}>
              {extendLoading ? 'Starting…' : 'Extend +7s'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== FIX IT — GUIDED RE-PROMPT ==================== */}
        <Dialog open={fixItOpen} onClose={() => !fixItLoading && setFixItOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <AutoFixHigh sx={{ color: AI_COLOR, fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', flex: 1 }}>Fix this video's prompt</Typography>
            <IconButton size="small" disabled={fixItLoading} onClick={() => setFixItOpen(false)}><FaTimes size={14} /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Tell the AI what went wrong. It re-reads the original prompt and your product images, then makes surgical edits — keeping what worked.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              {['Product warped or distorted', "Colors don't match the product", 'Camera moves too fast', 'Not enough camera movement', "Scene doesn't fit the product", 'Lighting is off', 'Weird artifacts / glitches'].map((issue) => {
                const selected = fixItIssues.includes(issue);
                return (
                  <Chip
                    key={issue}
                    label={issue}
                    size="small"
                    clickable
                    onClick={() => setFixItIssues(prev => selected ? prev.filter(i => i !== issue) : [...prev, issue])}
                    sx={{
                      border: `1px solid ${selected ? AI_COLOR : theme.palette.divider}`,
                      bgcolor: selected ? alpha(AI_COLOR, 0.12) : 'transparent',
                      color: selected ? AI_COLOR : 'text.primary',
                      fontWeight: selected ? 600 : 400,
                    }}
                  />
                );
              })}
            </Box>
            <TextField
              multiline
              rows={2}
              fullWidth
              size="small"
              label="Anything else? (optional)"
              placeholder="e.g. the rug's medallion pattern melts halfway through"
              value={fixItNotes}
              onChange={(e) => setFixItNotes(e.target.value)}
            />
            {historyItems[playingHistoryIndex]?.engine === 'omni' && historyItems[playingHistoryIndex]?.omniInteractionId && (
              <Tooltip title="On: Omni revises this exact video in place, keeping everything that was right (recommended). Off: only rewrite the prompt for a fresh generation.">
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, cursor: 'pointer' }}
                  onClick={() => setFixItEditInPlace(v => !v)}>
                  <Switch size="small" checked={fixItEditInPlace}
                    onChange={(e) => setFixItEditInPlace(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AI_COLOR },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: AI_COLOR } }} />
                  <Typography variant="caption" sx={{ color: fixItEditInPlace ? AI_TEXT : 'text.secondary', userSelect: 'none' }}>
                    Edit the video in place (Omni) — preserves what's already right
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button disabled={fixItLoading} onClick={() => setFixItOpen(false)} sx={{ color: theme.palette.text.secondary }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleFixIt}
              disabled={fixItLoading || (fixItIssues.length === 0 && !fixItNotes.trim())}
              startIcon={fixItLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AutoFixHigh sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}
            >
              {fixItLoading ? 'Working…'
                : (fixItEditInPlace && historyItems[playingHistoryIndex]?.engine === 'omni' && historyItems[playingHistoryIndex]?.omniInteractionId)
                  ? 'Edit video' : 'Revise prompt'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ==================== HISTORY SIDE-BY-SIDE VIDEO COMPARE ==================== */}
        <Dialog open={historyCompareOpen} onClose={() => setHistoryCompareOpen(false)} maxWidth="xl" fullWidth
          PaperProps={{ sx: { bgcolor: isDark ? '#0a0a0a' : '#fff' } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${theme.palette.divider}`, py: 1.5 }}>
            <MdCompare size={18} color={AI_COLOR} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', flex: 1 }}>Compare Videos</Typography>
            <IconButton size="small" onClick={() => setHistoryCompareOpen(false)}><FaTimes size={16} /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mt: 1 }}>
              {historyCompareSel.map((v, i) => (
                <Box key={(v.generationId || v.id) || i} sx={{ flex: '1 1 380px', minWidth: 0 }}>
                  <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}`, bgcolor: '#000' }}>
                    <video controls autoPlay loop muted playsInline src={v.s3Url}
                      style={{ width: '100%', maxHeight: '70vh', display: 'block' }} />
                  </Box>
                  <Typography variant="caption" noWrap sx={{ display: 'block', mt: 0.7, color: theme.palette.text.secondary }}>
                    {v.videoFileName || `Video ${v.generationId || v.id}`}
                  </Typography>
                  <Typography variant="caption" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', color: theme.palette.text.secondary, fontSize: 10.5, lineHeight: 1.4 }}>
                    {v.prompt || ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>
        </Dialog>

        {/* Fullscreen comparison modal: reference input(s) vs generated video */}
        <Dialog open={!!compareDialogItem} onClose={() => { setCompareDialogItem(null); setZoomImageUrl(null); setInplaceZoomIdx(null); }} fullScreen
          PaperProps={{ sx: { bgcolor: isDark ? '#0a0a0a' : '#ffffff' } }}>
          {compareDialogItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Top bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, bgcolor: isDark ? 'rgba(0,0,0,0.6)' : '#f4f4f6', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : theme.palette.divider}`, flexShrink: 0 }}>
                <MdCompare size={20} color={AI_COLOR} />
                <Typography sx={{ color: theme.palette.text.primary, fontWeight: 600, fontSize: '0.95rem', flex: 1 }}>
                  Compare — Reference vs Generated
                </Typography>
                <ToggleButtonGroup value={compareSplit} exclusive size="small" onChange={(_, v) => { if (v) setCompareSplit(v); }}
                  sx={{
                    '& .MuiToggleButton-root': {
                      color: theme.palette.text.secondary, borderColor: isDark ? 'rgba(255,255,255,0.2)' : theme.palette.divider,
                      textTransform: 'none', fontSize: 12, py: 0.3, px: 1.4,
                      '&.Mui-selected': { color: AI_TEXT, bgcolor: alpha(AI_COLOR, isDark ? 0.35 : 0.15), fontWeight: 600, '&:hover': { bgcolor: alpha(AI_COLOR, isDark ? 0.45 : 0.25) } },
                    },
                  }}>
                  <ToggleButton value="50">50 / 50</ToggleButton>
                  <ToggleButton value="75">Video 75%</ToggleButton>
                  <ToggleButton value="img">Image 75%</ToggleButton>
                </ToggleButtonGroup>
                <IconButton onClick={() => { setCompareDialogItem(null); setZoomImageUrl(null); setInplaceZoomIdx(null); }} sx={{ color: theme.palette.text.primary }}>
                  <FaTimes size={18} />
                </IconButton>
              </Box>
              {/* Body split */}
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1, p: 1 }}>
                {/* Reference images (click to zoom) */}
                <Box sx={{ flexGrow: compareSplit === 'img' ? 3 : 1, flexBasis: 0, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'auto', alignItems: 'center', justifyContent: 'center', p: 1 }}>
                  {(compareDialogItem.inputImages || []).map((url, idx) => {
                    const zoomedHere = inplaceZoomIdx === idx;
                    return (
                      <Box key={idx}
                        onClick={() => setInplaceZoomIdx(zoomedHere ? null : idx)}
                        onMouseMove={(e) => {
                          if (!zoomedHere) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = ((e.clientX - rect.left) / rect.width) * 100;
                          const y = ((e.clientY - rect.top) / rect.height) * 100;
                          const ov = e.currentTarget.querySelector('.inplace-ov');
                          if (ov) ov.style.backgroundPosition = `${x}% ${y}%`;
                        }}
                        sx={{ position: 'relative', maxWidth: '100%', flexShrink: 0, overflow: 'hidden', borderRadius: 1, cursor: zoomedHere ? 'zoom-out' : 'pointer' }}
                      >
                        <img src={url} alt={`Reference ${idx + 1}`}
                          style={{ maxWidth: '100%', maxHeight: (compareDialogItem.inputImages.length > 1 ? '42vh' : '84vh'), objectFit: 'contain', borderRadius: 4, display: 'block' }} />
                        {zoomedHere && (
                          <div className="inplace-ov" style={{
                            position: 'absolute', inset: 0,
                            backgroundImage: `url("${url}")`,
                            backgroundSize: '250%', backgroundRepeat: 'no-repeat', backgroundPosition: '50% 50%',
                            borderRadius: 4,
                          }} />
                        )}
                        <Tooltip title="Open full size">
                          <IconButton size="small"
                            onClick={(e) => { e.stopPropagation(); setInplaceZoomIdx(null); openImagePreview((compareDialogItem.inputImages || []).map(u => ({ src: u, label: fileNameFromUrl(u) })), idx); }}
                            sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', zIndex: 2, '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}>
                            <Fullscreen sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                  <Typography sx={{ color: theme.palette.text.secondary, fontSize: 11, mt: 0.5, flexShrink: 0 }}>
                    Click image to zoom here · expand icon opens full size
                  </Typography>
                </Box>
                {/* Generated video — width:100% so it enlarges as the column widens (75% mode) */}
                <Box sx={{ flexGrow: compareSplit === '75' ? 3 : 1, flexBasis: 0, minHeight: 0, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isDark ? '#000' : '#f0f0f2', border: isDark ? 'none' : `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden' }}>
                  <video controls autoPlay loop muted playsInline src={compareDialogItem.s3Url}
                    style={{ width: '100%', maxHeight: '86vh', objectFit: 'contain', display: 'block' }}>
                    Your browser does not support the video tag.
                  </video>
                </Box>
              </Box>
            </Box>
          )}
        </Dialog>

        {/* Full-size lightbox — fullScreen so the image is never clipped; X floats above the image's top-right like the AI Image Generator */}
        <Dialog open={!!zoomImageUrl} onClose={() => { setZoomImageUrl(null); setZoomActive(false); }} fullScreen
          sx={{ zIndex: 1600 }}
          PaperProps={{ sx: { bgcolor: 'transparent' } }}
          slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.2)' } } }}>
          <Box onClick={() => { setZoomImageUrl(null); setZoomActive(false); }}
            sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {zoomImageList.length > 1 && zoomImageUrl && (
              <Typography sx={{ position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)', color: '#fff', bgcolor: 'rgba(0,0,0,0.55)', px: 1.5, py: 0.4, borderRadius: 1, fontSize: 12, zIndex: 9999, pointerEvents: 'none' }}>
                {zoomImageList.findIndex(i => i.src === zoomImageUrl) + 1} / {zoomImageList.length}
              </Typography>
            )}
            {zoomImageUrl && (
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                {zoomImageList.length > 1 && (
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); stepImagePreview(-1); }}
                    sx={{ position: 'absolute', left: -52, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, zIndex: 3 }}>
                    <FaChevronLeft size={20} />
                  </IconButton>
                )}
                {zoomImageList.length > 1 && (
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); stepImagePreview(1); }}
                    sx={{ position: 'absolute', right: -52, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, zIndex: 3 }}>
                    <FaChevronRight size={20} />
                  </IconButton>
                )}
                <IconButton onClick={(e) => { e.stopPropagation(); setZoomImageUrl(null); setZoomActive(false); }}
                  sx={{ position: 'absolute', top: -42, right: 0, color: 'white', zIndex: 2, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.9))' }}>
                  <FaTimes size={20} />
                </IconButton>
                <Box
                  sx={{ position: 'relative', display: 'block', overflow: 'hidden', borderRadius: 1, lineHeight: 0, cursor: zoomActive ? 'zoom-out' : 'zoom-in' }}
                  onClick={(e) => { e.stopPropagation(); setZoomActive(z => !z); }}
                  onMouseMove={(e) => {
                    if (!zoomActive) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = ((e.clientX - rect.left) / rect.width) * 100;
                    const y = ((e.clientY - rect.top) / rect.height) * 100;
                    const ov = e.currentTarget.querySelector('.zoom-ov');
                    if (ov) ov.style.backgroundPosition = `${x}% ${y}%`;
                  }}
                >
                  <img src={zoomImageUrl} alt="Reference zoom"
                    style={{ maxWidth: '92vw', maxHeight: '84vh', objectFit: 'contain', borderRadius: 4, display: 'block' }} />
                  {zoomActive && (
                    <div className="zoom-ov" style={{
                      position: 'absolute', inset: 0,
                      backgroundImage: `url("${zoomImageUrl}")`,
                      backgroundSize: '250%', backgroundRepeat: 'no-repeat', backgroundPosition: '50% 50%',
                      borderRadius: 4,
                    }} />
                  )}
                  {!zoomActive && (
                    <div style={{
                      position: 'absolute', bottom: 8, right: 8,
                      backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                      padding: '4px 10px', borderRadius: 4, fontSize: '0.7rem',
                      pointerEvents: 'none',
                    }}>Click to zoom</div>
                  )}
                </Box>
                {(zoomImageList.find(i => i.src === zoomImageUrl)?.label || '') && (
                  <Typography noWrap sx={{ color: '#fff', mt: 1, fontSize: '0.85rem', textAlign: 'center', maxWidth: '92vw', textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                    {zoomImageList.find(i => i.src === zoomImageUrl)?.label}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AIVideoGenerator;
